import type { ManifoldToplevel } from 'manifold-3d';
import * as THREE from 'three';
import { tokenize } from '../scad/tokens';
import { parse } from '../scad/parser';
import { evaluate, createExpressionEvaluator, partWorldMesh, type GeometryPart } from '../scad/evaluator';
import { manifoldToGeometry } from '../engine/ManifoldEngine';
import { createEditor, type EditorInstance } from '../editor/setup';
import { ViewportManager, type TransformEndEvent } from '../viewport/ViewportManager';
import { SCAD_EXAMPLES } from '../scad/examples';
import type { ScadError, SourcePosition } from '../scad/errors';
import { stepMeshesToScad } from '../scad/stepToScad';
import type { Expression, ModuleCallStatement, Program, Statement } from '../scad/ast';
import type { DimensionSetup, DimensionLine, DragHandle, VectorContext } from '../viewport/DimensionOverlay';
import { saveMesh, loadAllMeshes } from './meshStore';
import { type ThemeMode, getCurrentTheme, getViewportTheme } from './theme';
import { loadDefaultFont } from '../scad/fonts';
import { minimalEdit } from './codeEdit';
import { buildSymbolTable } from '../editor/symbols';
import { sliceCaps, sectionPlaneThree, type SectionState } from './section';
import { findAncestors, frameOf, solve3, type V3 } from './snap';

export interface ParamField {
  name: string;           // parameter name (e.g., "r", "h", "size")
  value: string;          // current value as source text
  from: number;           // source offset for replacement
  to: number;
  type: 'number' | 'vector' | 'boolean' | 'string' | 'anchor' | 'axis' | 'readonly';
}

export interface ParamInfo {
  moduleName: string;     // e.g., "cylinder", "cube", "attach", "align"
  params: ParamField[];
}

export interface TransformInfo {
  position: [number, number, number];         // SCAD Z-up coords (from bbox center)
  rotation: [number, number, number] | null;  // from rotate() in chain, or null
  relativeToOrigin: boolean;                  // true when position is user-origin-relative
  size: [number, number, number];             // SCAD bbox size (X, Y, Z)
}

/** Axis-aligned box in SCAD coordinates (Z-up). */
export interface ScadBounds {
  min: [number, number, number];
  max: [number, number, number];
}

/** Solid metrics in SCAD units (mm, mm², mm³). */
export interface SolidMetrics {
  bounds: ScadBounds;
  volume: number;
  area: number;
}

export type { ScadError } from '../scad/errors';

export class EditorApp {
  private wasm: ManifoldToplevel | null = null;
  private editor: EditorInstance;
  private viewport: ViewportManager;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pipelineRunning = false;
  /** Latest code submitted while a pipeline run was in progress — rendered
   *  as soon as the current run finishes instead of being dropped. */
  private queuedCode: string | null = null;
  private afterRenderCallbacks: Array<() => void> = [];
  private lastErrors: ScadError[] = [];
  /** Model bounds of the last render (union of part boxes), SCAD coords. */
  private modelBounds: ScadBounds | null = null;
  /** Metrics captured for single-manifold renders (no parts to re-union). */
  private singleMetrics: SolidMetrics | null = null;
  private section: SectionState | null = null;
  private sectionArea = 0;
  private lastEchoLog: string[] = [];
  private fn = 32;
  private statsCallback: ((vertices: number, faces: number, timeMs: number) => void) | null = null;
  private lastManifold: InstanceType<ManifoldToplevel['Manifold']> | null = null;
  private lastParts: GeometryPart[] = [];
  private lastProgram: Program | null = null;
  private suppressNextCodeChange = false;
  private suppressCursorPick = false;
  private suppressHighlightFromViewport = false;
  private selectionCallbacks: Array<(from: SourcePosition | null, to: SourcePosition | null) => void> = [];
  private importedManifolds = new Map<string, InstanceType<ManifoldToplevel['Manifold']>>();
  private paramCallback: ((infos: ParamInfo[] | null) => void) | null = null;
  private errorCallback: ((errors: ScadError[], echoLog?: string[]) => void) | null = null;
  private transformCallback: ((info: TransformInfo | null) => void) | null = null;
  private lastRotation: [number, number, number] | null = null;
  private codeUpdateCallback: ((code: string) => void) | null = null;
  private renderingCallback: ((rendering: boolean) => void) | null = null;
  private showOriginDims: boolean = (() => {
    try { return localStorage.getItem('scadplus-origin-dims') !== '0'; } catch { return true; }
  })();
  private lastDimChain: ParamInfo[] | null = null;

  constructor(
    editorContainer: HTMLElement,
    viewportContainer: HTMLElement,
    options?: { viewOnly?: boolean },
  ) {
    const initialTheme = getCurrentTheme();
    this.viewport = new ViewportManager(viewportContainer);
    this.viewport.applyTheme(getViewportTheme(initialTheme));
    this.editor = createEditor(
      editorContainer,
      SCAD_EXAMPLES[0].code,
      (code) => this.onCodeChange(code),
      initialTheme,
    );
    this.scheduleSymbolRefresh();

    if (options?.viewOnly) {
      this.viewport.setViewOnly(true);
      return;
    }

    // Wire up transform-end to code sync
    this.viewport.onTransformEnd((event) => this.handleTransformEnd(event));

    // Editor cursor → 3D selection. The deepest-enclosing shape whose source
    // range contains the caret becomes selected in the viewport, so deeply
    // nested objects can be picked just by clicking their line in the code.
    // The pick does NOT move the caret back — we set a flag so the viewport's
    // child-selected callback knows to skip editor.highlightRange.
    let lastCursorPickOffset = -1;
    this.editor.onCursorChange((offset) => {
      if (this.suppressCursorPick) return;
      if (offset === lastCursorPickOffset) return;
      lastCursorPickOffset = offset;
      this.suppressHighlightFromViewport = true;
      try {
        this.viewport.selectByCursorOffset(offset);
      } finally {
        // Give the synchronous child-selected callback a chance to fire
        setTimeout(() => { this.suppressHighlightFromViewport = false; }, 0);
      }
    });

    // Wire up child selection for parameter editing
    this.viewport.onChildSelected((from, to) => this.handleChildSelected(from, to));
    this.viewport.onSelectionCleared(() => {
      this.editor.highlightRange(0, 0);
      if (this.paramCallback) this.paramCallback(null);
      if (this.transformCallback) this.transformCallback(null);
      for (const cb of this.selectionCallbacks) cb(null, null);
      this.lastRotation = null;
      this.lastDimChain = null;
      this.viewport.clearDimensions();
    });

    // Wire up dimension edit callback
    this.viewport.onDimensionEdit((from, to, value) => this.updateParam(from, to, value));

    // Wire up live transform updates (fires during gizmo drag)
    this.viewport.onTransformLive(() => this.fireTransformInfoFromBBox());

    // Re-fire readout + dimension overlay whenever the user origin moves
    this.viewport.onOriginChange(() => {
      this.fireTransformInfoFromBBox();
      this.redrawDimensions();
    });
  }

  async initWasm(): Promise<void> {
    const Module = (await import('manifold-3d')).default;
    const [wasm] = await Promise.all([Module(), loadDefaultFont()]);
    this.wasm = wasm;
    this.wasm.setup();
    // Restore any previously imported meshes from IndexedDB
    await this.restoreImports();
    // Run initial code after WASM loads
    this.runPipeline(this.editor.getCode());
  }

  private async restoreImports(): Promise<void> {
    if (!this.wasm) return;
    try {
      const records = await loadAllMeshes();
      for (const rec of records) {
        const mesh = new this.wasm.Mesh({
          numProp: rec.numProp,
          vertProperties: rec.vertProperties,
          triVerts: rec.triVerts,
        });
        const manifold = new this.wasm.Manifold(mesh);
        this.importedManifolds.set(rec.filename, manifold);
      }
      if (records.length > 0) {
        console.log(`Restored ${records.length} imported mesh(es) from IndexedDB`);
      }
    } catch (e) {
      console.warn('Failed to restore imported meshes:', e);
    }
  }

  onStatsUpdate(callback: (vertices: number, faces: number, timeMs: number) => void): void {
    this.statsCallback = callback;
  }

  onErrorUpdate(callback: (errors: ScadError[], echoLog?: string[]) => void): void {
    this.errorCallback = callback;
  }

  onCodeUpdate(callback: (code: string) => void): void {
    this.codeUpdateCallback = callback;
  }

  onRenderingUpdate(cb: (rendering: boolean) => void): void {
    this.renderingCallback = cb;
  }

  private symbolTimer: ReturnType<typeof setTimeout> | null = null;

  /** Re-parse the editor text (no evaluation) shortly after edits so
   *  completion / hover / F12 know the user's modules, functions, variables. */
  private scheduleSymbolRefresh(): void {
    if (this.symbolTimer) clearTimeout(this.symbolTimer);
    this.symbolTimer = setTimeout(() => {
      this.symbolTimer = null;
      const code = this.editor.getCode();
      try {
        const { ast } = parse(tokenize(code).tokens);
        this.editor.setSymbols(buildSymbolTable(ast, code));
      } catch { /* keep the previous table while the code is mid-edit */ }
    }, 150);
  }

  private onCodeChange(code: string): void {
    this.scheduleSymbolRefresh();
    if (this.suppressNextCodeChange) {
      this.suppressNextCodeChange = false;
      return;
    }
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (this.codeUpdateCallback) this.codeUpdateCallback(code);
      requestAnimationFrame(() => setTimeout(() => this.runPipeline(code), 0));
    }, 500);
  }

  private includeCache = new Map<string, string>();

  private async resolveIncludes(code: string, depth = 0, seen = new Set<string>()): Promise<string> {
    if (depth > 5) return code;
    const re = /include\s*<([^>]+)>\s*;?/g;
    const matches: { start: number; end: number; url: string }[] = [];
    let m: RegExpExecArray | null;
    while ((m = re.exec(code)) !== null) {
      matches.push({ start: m.index, end: m.index + m[0].length, url: m[1] });
    }
    if (matches.length === 0) return code;

    // Process in reverse to preserve offsets
    let result = code;
    for (let i = matches.length - 1; i >= 0; i--) {
      const { start, end, url } = matches[i];
      if (seen.has(url)) {
        result = result.substring(0, start) + `/* circular include: ${url} */` + result.substring(end);
        continue;
      }
      try {
        let content: string;
        if (this.includeCache.has(url)) {
          content = this.includeCache.get(url)!;
        } else {
          const resp = await fetch(url);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const contentType = resp.headers.get('content-type') || '';
          if (contentType.includes('text/html')) throw new Error('URL returned HTML, not SCAD');
          content = await resp.text();
          // Reject content that looks like HTML (e.g. error pages, GitHub file views)
          const trimmed = content.trimStart();
          if (trimmed.startsWith('<!') || trimmed.startsWith('<html') || trimmed.startsWith('<HTML')) {
            throw new Error('URL returned HTML, not SCAD');
          }
          this.includeCache.set(url, content);
        }
        const newSeen = new Set(seen);
        newSeen.add(url);
        content = await this.resolveIncludes(content, depth + 1, newSeen);
        result = result.substring(0, start) + content + result.substring(end);
      } catch (e) {
        result = result.substring(0, start) + `/* failed to include: ${url} */` + result.substring(end);
      }
    }
    return result;
  }

  /**
   * Run tokenize → parse → evaluate → render. Runs never overlap: code
   * submitted while a run is in progress is queued (latest wins) and
   * rendered right after. When the queue drains, `afterNextRender`
   * callbacks fire and `scadplus:render-complete` is dispatched with the
   * final errors/echo log.
   */
  async runPipeline(code: string): Promise<void> {
    if (!this.wasm) return;
    if (this.pipelineRunning) {
      this.queuedCode = code;
      return;
    }
    let next: string | null = code;
    while (next !== null) {
      this.queuedCode = null;
      await this.runPipelineOnce(next);
      next = this.queuedCode;
    }
    if (this.section) this.updateSection();
    const callbacks = this.afterRenderCallbacks.splice(0);
    for (const cb of callbacks) {
      try { cb(); } catch (e) { console.error('afterNextRender callback failed:', e); }
    }
    window.dispatchEvent(new CustomEvent('scadplus:render-complete', {
      detail: { errors: this.lastErrors, echoLog: this.lastEchoLog },
    }));
  }

  /**
   * Show a section view: clip the model at a plane perpendicular to a SCAD
   * axis and draw the exact cut faces (Manifold slices of the parts).
   * Pass null to turn it off. Recomputed automatically after each render.
   */
  setSection(state: SectionState | null): void {
    this.section = state ? { ...state } : null;
    this.updateSection();
  }

  getSection(): SectionState | null {
    return this.section ? { ...this.section } : null;
  }

  /** Cut area (mm²) of the current section, 0 when off. */
  getSectionArea(): number {
    return this.section ? this.sectionArea : 0;
  }

  private updateSection(): void {
    if (!this.section || !this.wasm) {
      this.viewport.setSectionPlane(null);
      this.viewport.setSectionCaps(null);
      this.sectionArea = 0;
      return;
    }
    const { normal, constant } = sectionPlaneThree(this.section);
    this.viewport.setSectionPlane(new THREE.Plane(new THREE.Vector3(...normal), constant));
    try {
      const { triangles, area } = sliceCaps(this.wasm, this.lastParts.map(p => p.manifold), this.section);
      this.sectionArea = area;
      this.viewport.setSectionCaps(triangles);
    } catch (e) {
      console.warn('Section slice failed:', e);
      this.sectionArea = 0;
      this.viewport.setSectionCaps(null);
    }
  }

  /**
   * The selected part's source, for AI context: line range + text
   * (truncated), or null when nothing is selected.
   */
  getSelectionContext(maxChars = 1500): { fromLine: number; toLine: number; text: string } | null {
    const range = this.viewport.getSelectedRange();
    if (!range) return null;
    const code = this.editor.getCode();
    // Statement ranges can run past the trailing newline/whitespace
    const text = code.slice(range.from, range.to).trimEnd();
    const line = (off: number) => code.slice(0, off).split('\n').length;
    return {
      fromLine: line(range.from),
      toLine: line(range.from + text.length),
      text: text.length > maxChars ? `${text.slice(0, maxChars)}\n…` : text,
    };
  }

  /** Insert text at the editor cursor (own lines), as one undoable edit. */
  insertAtCursor(text: string): void {
    const view = this.editor.view;
    const pos = view.state.selection.main.head;
    const line = view.state.doc.lineAt(pos);
    // Insert after the current line so a snippet never splits a statement
    const at = line.to;
    this.editor.replaceRange(at, at, `\n${text.trimEnd()}\n`);
  }

  /**
   * Plain-text summary of the last render for AI context: overall size and
   * bounds plus the size/position of each top-level part (by source line).
   * Cheap — part manifolds are already computed.
   */
  getRenderSummary(maxParts = 12): string | null {
    const b = this.modelBounds;
    if (!b) return null;
    const f = (v: number) => (Math.abs(v) < 0.005 ? '0' : String(Math.round(v * 100) / 100));
    const size = (min: ArrayLike<number>, max: ArrayLike<number>) => `${f(max[0] - min[0])} × ${f(max[1] - min[1])} × ${f(max[2] - min[2])} mm`;
    const range = (min: ArrayLike<number>, max: ArrayLike<number>) =>
      `X ${f(min[0])}..${f(max[0])}, Y ${f(min[1])}..${f(max[1])}, Z ${f(min[2])}..${f(max[2])}`;
    const lines = [`Overall size ${size(b.min, b.max)} (${range(b.min, b.max)}); ${this.lastParts.length} top-level part(s).`];
    for (const p of this.lastParts.slice(0, maxParts)) {
      const pb = p.manifold.boundingBox();
      lines.push(`- part at line ${p.from.line}: ${size(pb.min, pb.max)} (${range(pb.min, pb.max)})`);
    }
    if (this.lastParts.length > maxParts) lines.push(`- … ${this.lastParts.length - maxParts} more`);
    return lines.join('\n');
  }

  /** Bounds of the whole model from the last render (cheap), or null. */
  getModelBounds(): ScadBounds | null {
    return this.modelBounds;
  }

  /**
   * Volume / surface area of the whole model. Computed on demand: the
   * top-level parts are unioned (overlaps counted once) — not done during
   * normal renders because the union can be expensive for large models.
   */
  computeModelMetrics(): SolidMetrics | null {
    if (this.singleMetrics) return this.singleMetrics;
    if (!this.wasm || this.lastParts.length === 0) return null;
    const manifolds = this.lastParts.map(p => p.manifold);
    if (manifolds.length === 1) return metricsOf(manifolds[0]);
    const union = this.wasm.Manifold.union(manifolds);
    try {
      return metricsOf(union);
    } finally {
      union.delete();
    }
  }

  /** Metrics of the part or ghost child whose source range is [from, to). */
  computeRangeMetrics(from: number, to: number, instance?: string): SolidMetrics | null {
    for (const p of this.lastParts) {
      if (p.from.offset === from && p.to.offset === to) return metricsOf(p.manifold);
      const child = p.children?.find(c => c.from.offset === from && c.to.offset === to && (instance === undefined || c.instance === instance))
        ?? p.children?.find(c => c.from.offset === from && c.to.offset === to);
      if (child) return child.matrix ? ghostMetrics(child) : metricsOf(child.manifold);
    }
    return null;
  }

  /** The user module whose body contains `offset`, and how often it is called. */
  getEnclosingModule(offset: number): { name: string; uses: number } | null {
    const program = this.lastProgram;
    if (!program) return null;
    let found: string | null = null;
    const visit = (stmts: Statement[]) => {
      for (const st of stmts) {
        if (st.type === 'ModuleDefinitionStatement') {
          if (st.from.offset < offset && offset < st.to.offset) found = st.name;
          visit(st.body);
        } else if (st.type === 'ModuleCallStatement') visit(st.children);
        else if (st.type === 'IfStatement') { visit(st.thenBody); visit(st.elseBody); }
        else if (st.type === 'ForStatement') visit(st.body);
      }
    };
    visit(program.statements);
    if (!found) return null;
    let uses = 0;
    const count = (stmts: Statement[]) => {
      for (const st of stmts) {
        if (st.type === 'ModuleCallStatement') { if (st.name === found) uses++; count(st.children); }
        else if (st.type === 'ModuleDefinitionStatement') count(st.body);
        else if (st.type === 'IfStatement') { count(st.thenBody); count(st.elseBody); }
        else if (st.type === 'ForStatement') count(st.body);
      }
    };
    count(program.statements);
    return { name: found, uses };
  }

  /** Run `cb` once, after the pipeline has finished rendering the latest
   *  submitted code (e.g. to re-select a part after a code edit). */
  afterNextRender(cb: () => void): void {
    this.afterRenderCallbacks.push(cb);
  }

  getLastErrors(): ScadError[] {
    return this.lastErrors;
  }

  getLastEchoLog(): string[] {
    return this.lastEchoLog;
  }

  private async runPipelineOnce(code: string): Promise<void> {
    this.pipelineRunning = true;
    if (this.renderingCallback) this.renderingCallback(true);
    try {
      await this.runPipelineBody(code);
    } finally {
      if (this.renderingCallback) this.renderingCallback(false);
      this.pipelineRunning = false;
    }
  }

  private async runPipelineBody(code: string): Promise<void> {
    if (!this.wasm) return;
    this.modelBounds = null;
    this.singleMetrics = null;

    // Resolve remote includes before tokenizing
    try {
      code = await this.resolveIncludes(code);
    } catch { /* ignore include errors, proceed with original code */ }

    const startTime = performance.now();
    const allErrors: ScadError[] = [];

    // Tokenize
    const { tokens, errors: tokenErrors } = tokenize(code);
    allErrors.push(...tokenErrors);

    // Parse
    const { ast, errors: parseErrors } = parse(tokens);
    allErrors.push(...parseErrors);
    this.lastProgram = ast;

    // Evaluate (even if there are parse errors, try to evaluate what we can)
    let echoLog: string[] | undefined;
    if (ast.statements.length > 0) {
      // Free previous manifolds
      this.freeManifolds();

      const evalResult = evaluate(ast, this.wasm, this.fn, this.importedManifolds);
      const { manifold, parts, debugManifold, errors: evalErrors, frameBoxes } = evalResult;
      echoLog = evalResult.echoLog;
      allErrors.push(...evalErrors);

      // Store parts (with AST statements) for transform editing
      this.lastParts = parts;

      if (parts.length > 0) {
        // Build per-part geometries (don't delete manifolds — they're tracked by lastParts)
        const partGeometries: Array<{
          geometry: THREE.BufferGeometry;
          from: typeof parts[0]['from'];
          to: typeof parts[0]['to'];
          children?: Array<{ geometry: THREE.BufferGeometry; from: typeof parts[0]['from']; to: typeof parts[0]['to']; isAttach?: boolean; isParentBody?: boolean; isCut?: boolean; color?: [number, number, number]; opacity?: number; instance?: string; nested?: boolean }>;
          operation?: 'union' | 'difference' | 'intersection';
          color?: [number, number, number];
          opacity?: number;
          display?: Array<{ geometry: THREE.BufferGeometry; color?: [number, number, number]; opacity?: number }>;
        }> = [];
        let totalVertices = 0;
        let totalFaces = 0;

        for (const part of parts) {
          const geometry = manifoldToGeometry(part.manifold, false);

          // Build child geometries for boolean groups (used for ghost mesh raycasting)
          let childGeometries: Array<{ geometry: THREE.BufferGeometry; from: typeof parts[0]['from']; to: typeof parts[0]['to']; isAttach?: boolean; isParentBody?: boolean; isCut?: boolean; color?: [number, number, number]; opacity?: number; instance?: string; nested?: boolean }> | undefined;
          if (part.children && part.children.length > 0) {
            childGeometries = part.children.map(child => ({
              geometry: ghostGeometry(child),
              from: child.from,
              to: child.to,
              isAttach: child.isAttach,
              isParentBody: child.isParentBody,
              isCut: child.isCut,
              color: child.color,
              opacity: child.opacity,
              instance: child.instance,
              nested: child.nested,
            }));
          }

          partGeometries.push({
            geometry, from: part.from, to: part.to, children: childGeometries, operation: part.operation,
            color: part.color, opacity: part.opacity,
            display: part.display?.map(d => ({ geometry: ghostGeometry(d), color: d.color, opacity: d.opacity })),
          });

          const posAttr = geometry.getAttribute('position');
          const indexAttr = geometry.getIndex();
          totalVertices += posAttr ? posAttr.count : 0;
          totalFaces += indexAttr ? indexAttr.count / 3 : (posAttr ? posAttr.count / 3 : 0);
        }

        let debugGeometry: THREE.BufferGeometry | undefined;
        if (debugManifold) {
          const isPartManifold = parts.some(p => p.manifold === debugManifold);
          debugGeometry = manifoldToGeometry(debugManifold, !isPartManifold && debugManifold !== manifold);
        }

        // Free the unioned manifold if it differs from parts
        if (manifold && !parts.some(p => p.manifold === manifold)) {
          try { manifold.delete(); } catch { /* ok */ }
        }

        const timeMs = performance.now() - startTime;
        this.modelBounds = unionBounds(parts.map(p => p.manifold.boundingBox()));
        this.viewport.showParts(partGeometries, debugGeometry);

        if (this.statsCallback) {
          this.statsCallback(totalVertices, totalFaces, timeMs);
        }
        window.umami?.track('render_model', { vertices: totalVertices, faces: totalFaces, timeMs: Math.round(timeMs) });
      } else if (manifold) {
        let debugGeometry: THREE.BufferGeometry | undefined;
        if (debugManifold) {
          debugGeometry = manifoldToGeometry(debugManifold, debugManifold !== manifold);
        }
        this.singleMetrics = {
          bounds: toScadBounds(manifold.boundingBox()),
          volume: manifold.volume(),
          area: manifold.surfaceArea(),
        };
        this.modelBounds = this.singleMetrics.bounds;
        const geometry = manifoldToGeometry(manifold);
        const timeMs = performance.now() - startTime;

        const posAttr = geometry.getAttribute('position');
        const indexAttr = geometry.getIndex();
        const vertexCount = posAttr ? posAttr.count : 0;
        const faceCount = indexAttr ? indexAttr.count / 3 : vertexCount / 3;

        this.viewport.showGeometry(geometry, debugGeometry);

        if (this.statsCallback) {
          this.statsCallback(vertexCount, faceCount, timeMs);
        }
      } else if (debugManifold) {
        const debugGeometry = manifoldToGeometry(debugManifold);
        this.viewport.showGeometry(debugGeometry);
        if (this.statsCallback) {
          this.statsCallback(0, 0, performance.now() - startTime);
        }
      } else {
        this.viewport.removeMesh();
        if (this.statsCallback) {
          this.statsCallback(0, 0, performance.now() - startTime);
        }
      }

      // Show frame wireframe boxes (dashed lines for virtual bounding boxes)
      if (frameBoxes.length > 0) {
        this.viewport.showFrameBoxes(frameBoxes);
      }
    } else {
      this.viewport.removeMesh();
      if (this.statsCallback) {
        this.statsCallback(0, 0, performance.now() - startTime);
      }
    }

    // Show errors in editor and console
    if (allErrors.length > 0) {
      console.group(`SCAD: ${allErrors.length} error(s)`);
      for (const err of allErrors) {
        console.error(`[${err.severity}] line ${err.from.line}:${err.from.col} — ${err.message}`);
      }
      console.groupEnd();
    }
    this.editor.setErrors(allErrors);
    this.lastErrors = allErrors;
    this.lastEchoLog = echoLog ?? [];
    if (this.errorCallback) {
      this.errorCallback(allErrors, echoLog);
    }
  }

  // --- Transform → Code Sync ---

  /** Modules that position children but should not themselves be wrapped with transforms */
  private static POSITIONING_MODULES = new Set(['attach', 'align', 'position']);

  /**
   * Walk down through attach/align wrappers to find the innermost child
   * where a transform should be applied.
   * e.g. attach(LEFT, ...) cylinder(...) → target is the cylinder
   * e.g. attach(LEFT, ...) translate([1,0,0]) cylinder(...) → target is the translate
   */
  private findTransformTarget(stmt: Statement): Statement {
    let current = stmt;
    while (
      current.type === 'ModuleCallStatement' &&
      EditorApp.POSITIONING_MODULES.has((current as ModuleCallStatement).name)
    ) {
      const mcs = current as ModuleCallStatement;
      const regularChildren = mcs.children.filter(c =>
        !(c.type === 'ModuleCallStatement' && EditorApp.POSITIONING_MODULES.has((c as ModuleCallStatement).name))
      );
      if (regularChildren.length === 1) {
        current = regularChildren[0];
      } else {
        break;
      }
    }
    return current;
  }

  /**
   * Resolve the statement a transform edit for the selected source range
   * should wrap or update (walks past attach/align/position wrappers).
   */
  private resolveTransformTarget(fromOffset: number, toOffset: number): {
    target: Statement | undefined; targetFrom: number; targetTo: number; indent: string;
  } {
    const code = this.editor.getCode();
    // Find matching part or child with AST info
    let stmt: GeometryPart['stmt'] | undefined;
    for (const p of this.lastParts) {
      if (p.from.offset === fromOffset && p.to.offset === toOffset) {
        stmt = p.stmt;
        break;
      }
      // Also check children (ghost children in boolean groups)
      if (p.children) {
        const child = p.children.find(c => c.from.offset === fromOffset && c.to.offset === toOffset);
        if (child) {
          if (child.isParentBody) {
            // Parent body ghost: use the part's top-level stmt so we can
            // detect/update outer transform wrappers (e.g. existing translate)
            stmt = p.stmt;
          } else {
            // For non-attach body ghosts (nested cubes), find the wrapping
            // layout ghost so findTransformTarget can walk through it and
            // detect existing translate/rotate/scale wrappers.
            const wrapper = p.children.find(c =>
              c !== child &&
              c.stmt?.type === 'ModuleCallStatement' &&
              EditorApp.POSITIONING_MODULES.has((c.stmt as ModuleCallStatement).name) &&
              c.from.offset <= fromOffset && c.to.offset >= toOffset
            );
            stmt = wrapper ? wrapper.stmt : child.stmt;
          }
          break;
        }
      }
    }

    // Walk past attach/align wrappers to find the actual target for transforms
    const target = stmt ? this.findTransformTarget(stmt) : undefined;
    const targetFrom = target ? target.from.offset : fromOffset;
    const targetTo = target ? target.to.offset : toOffset;

    // Detect the indentation of the target statement
    const lineStart = code.lastIndexOf('\n', targetFrom - 1) + 1;
    const indent = code.substring(lineStart, targetFrom).match(/^(\s*)/)?.[1] ?? '';
    return { target, targetFrom, targetTo, indent };
  }

  private static fmtNum(n: number): string {
    const rounded = Math.round(n * 1000) / 1000;
    return rounded === 0 ? '0' : String(rounded);
  }

  /**
   * Move the part/child whose source range is [from, to) by `vec` (SCAD
   * axes, applied in the target statement's own frame): adds to an existing
   * literal translate() or wraps the target in a new one. Undoable; the
   * selection is restored after the re-render. Returns false for a ~zero move.
   */
  translateRange(from: number, to: number, vec: [number, number, number]): boolean {
    const EPS = 0.001;
    if (Math.abs(vec[0]) < EPS && Math.abs(vec[1]) < EPS && Math.abs(vec[2]) < EPS) return false;
    const code = this.editor.getCode();
    const { target, targetFrom, targetTo, indent } = this.resolveTransformTarget(from, to);
    const fmt = EditorApp.fmtNum;

    // If target is already translate(...), update its vector params
    if (target?.type === 'ModuleCallStatement' && (target as ModuleCallStatement).name === 'translate') {
      const existing = this.tryExtractVec3((target as ModuleCallStatement).args, 'v', 0);
      if (existing) {
        const newVec = `[${fmt(existing.values[0] + vec[0])}, ${fmt(existing.values[1] + vec[1])}, ${fmt(existing.values[2] + vec[2])}]`;
        this.applyCodeChange(code.substring(0, existing.from) + newVec + code.substring(existing.to));
        this.reselectAfterRender(from, this.viewport.getSelectedKey()?.instance);
        return true;
      }
    }

    // Fallback: wrap target with new translate
    const stmtText = code.substring(targetFrom, targetTo);
    this.applyCodeChange(
      code.substring(0, targetFrom) +
      `translate([${fmt(vec[0])}, ${fmt(vec[1])}, ${fmt(vec[2])}])\n${indent}  ${stmtText}` +
      code.substring(targetTo)
    );
    this.reselectAfterRender(from, this.viewport.getSelectedKey()?.instance);
    return true;
  }

  /**
   * Convert a world-space (SCAD) move of the part [from, to) into the frame
   * of the statement translateRange() edits — undoing enclosing rotate /
   * scale / mirror / multmatrix — or explain why that isn't possible.
   */
  resolveLocalMove(from: number, to: number, world: V3): { ok: true; local: V3 } | { ok: false; reason: string } {
    if (!this.lastProgram || !this.wasm) return { ok: false, reason: 'no rendered model' };
    const { targetFrom, targetTo } = this.resolveTransformTarget(from, to);
    const anc = findAncestors(this.lastProgram, targetFrom, targetTo);
    if ('reason' in anc) return { ok: false, reason: anc.reason };
    const frame = frameOf(anc.chain, createExpressionEvaluator(this.lastProgram, this.wasm));
    if (!frame.ok) return frame;
    const local = solve3(frame.linear, world);
    return local ? { ok: true, local } : { ok: false, reason: 'an enclosing transform is degenerate (scale 0?)' };
  }

  /** Move the part [from, to) by a world-space (SCAD) vector. Undoable. */
  moveRangeWorld(from: number, to: number, world: V3): { ok: true; moved: boolean } | { ok: false; reason: string } {
    const r = this.resolveLocalMove(from, to, world);
    if (!r.ok) return r;
    return { ok: true, moved: this.translateRange(from, to, r.local) };
  }

  private handleTransformEnd(event: TransformEndEvent): void {
    const code = this.editor.getCode();
    const EPS = 0.001;
    const fmt = EditorApp.fmtNum;

    switch (event.mode) {
      case 'translate': {
        // Convert Three.js Y-up delta to SCAD Z-up: [x, y, z] → [x, -z, y]
        const world: V3 = [event.position.x, -event.position.z, event.position.y];
        // Inside rotated/scaled groups the gizmo's world move must be
        // expressed in the target's own frame; where that frame can't be
        // resolved (module bodies, loops…) keep the previous behaviour.
        const r = this.resolveLocalMove(event.from.offset, event.to.offset, world);
        this.translateRange(event.from.offset, event.to.offset, r.ok ? r.local : world);
        break;
      }
      case 'rotate': {
        const { target, targetFrom, targetTo, indent } = this.resolveTransformTarget(event.from.offset, event.to.offset);
        const toDeg = (rad: number) => rad * 180 / Math.PI;
        if (Math.abs(event.rotation.x) < EPS && Math.abs(event.rotation.y) < EPS && Math.abs(event.rotation.z) < EPS) return;

        const scadRx = toDeg(event.rotation.x);
        const scadRy = toDeg(-event.rotation.z);
        const scadRz = toDeg(event.rotation.y);

        // If target is already rotate(...), update its vector params
        if (target?.type === 'ModuleCallStatement' && (target as ModuleCallStatement).name === 'rotate') {
          const existing = this.tryExtractVec3((target as ModuleCallStatement).args, 'a', 0);
          if (existing) {
            const newVec = `[${fmt(existing.values[0] + scadRx)}, ${fmt(existing.values[1] + scadRy)}, ${fmt(existing.values[2] + scadRz)}]`;
            this.applyCodeChange(
              code.substring(0, existing.from) + newVec + code.substring(existing.to)
            );
            return;
          }
        }

        // Fallback: wrap target with new rotate
        const stmtText = code.substring(targetFrom, targetTo);
        this.applyCodeChange(
          code.substring(0, targetFrom) +
          `rotate([${fmt(scadRx)}, ${fmt(scadRy)}, ${fmt(scadRz)}])\n${indent}  ${stmtText}` +
          code.substring(targetTo)
        );
        break;
      }
      case 'scale': {
        const { target, targetFrom, targetTo, indent } = this.resolveTransformTarget(event.from.offset, event.to.offset);
        if (Math.abs(event.scale.x - 1) < EPS && Math.abs(event.scale.y - 1) < EPS && Math.abs(event.scale.z - 1) < EPS) return;

        const scadSx = event.scale.x;
        const scadSy = event.scale.z;
        const scadSz = event.scale.y;

        // If target is already scale(...), update its vector params (multiplicative)
        if (target?.type === 'ModuleCallStatement' && (target as ModuleCallStatement).name === 'scale') {
          const existing = this.tryExtractVec3((target as ModuleCallStatement).args, 'v', 0);
          if (existing) {
            const newVec = `[${fmt(existing.values[0] * scadSx)}, ${fmt(existing.values[1] * scadSy)}, ${fmt(existing.values[2] * scadSz)}]`;
            this.applyCodeChange(
              code.substring(0, existing.from) + newVec + code.substring(existing.to)
            );
            return;
          }
        }

        // Fallback: wrap target with new scale
        const stmtText = code.substring(targetFrom, targetTo);
        this.applyCodeChange(
          code.substring(0, targetFrom) +
          `scale([${fmt(scadSx)}, ${fmt(scadSy)}, ${fmt(scadSz)}])\n${indent}  ${stmtText}` +
          code.substring(targetTo)
        );
        break;
      }
    }
  }

  /**
   * Try to extract a literal [x, y, z] vector from a module call's arguments.
   * Returns the numeric values and source offsets for replacement, or null if
   * the argument is not a simple numeric vector (e.g. uses variables/expressions).
   */
  private tryExtractVec3(
    args: { name?: string; value: Expression }[],
    name: string,
    positionalIndex: number,
  ): { values: [number, number, number]; from: number; to: number } | null {
    // Find by name first, then by position
    const arg = args.find(a => a.name === name) ||
      (positionalIndex < args.length && !args[positionalIndex].name ? args[positionalIndex] : undefined);
    if (!arg) return null;

    const expr = arg.value;
    if (expr.type !== 'VectorExpression' || expr.elements.length < 3) return null;

    const values: number[] = [];
    for (let i = 0; i < 3; i++) {
      const el = expr.elements[i];
      if (el.type === 'NumberLiteral') {
        values.push(el.value);
      } else if (el.type === 'UnaryExpression' && el.operator === '-' && el.operand.type === 'NumberLiteral') {
        values.push(-el.operand.value);
      } else {
        return null; // Not a simple literal — can't update in place
      }
    }

    return {
      values: [values[0], values[1], values[2]],
      from: expr.from.offset,
      to: expr.to.offset,
    };
  }

  // --- Child Selection → Parameter Panel ---

  private handleChildSelected(from: SourcePosition, to: SourcePosition): void {
    // If this selection was driven by the editor caret moving, leave the
    // caret alone. We still run the rest (param panel, dimension overlay).
    if (!this.suppressHighlightFromViewport) {
      this.suppressCursorPick = true;
      this.editor.highlightRange(from.offset, to.offset);
      setTimeout(() => { this.suppressCursorPick = false; }, 50);
    }
    for (const cb of this.selectionCallbacks) cb(from, to);

    const code = this.editor.getCode();

    // Helper: show dimensions for a param chain
    const showDims = (chain: ParamInfo[]) => {
      this.lastDimChain = chain;
      this.redrawDimensions();
    };

    // Helper: fire transform info for the current selection
    const fireTransformInfo = (chain: ParamInfo[]) => {
      if (!this.transformCallback) return;
      const geoInfo = this.viewport.getSelectedBBox();
      if (!geoInfo) {
        this.transformCallback(null);
        return;
      }
      const center = new THREE.Vector3();
      geoInfo.bbox.getCenter(center);
      const o = this.viewport.getUserOrigin();
      const relativeToOrigin = o.lengthSq() > 1e-6;
      const rel = relativeToOrigin
        ? new THREE.Vector3().subVectors(center, o)
        : center;
      // Convert Three.js Y-up to SCAD Z-up: [x, -z, y]
      const position: [number, number, number] = [rel.x, -rel.z, rel.y];

      // Scan chain for rotate() module to extract rotation values
      let rotation: [number, number, number] | null = null;
      const rotateInfo = chain.find(info => info.moduleName === 'rotate');
      if (rotateInfo) {
        const aParam = rotateInfo.params.find(p => p.name === 'a' || p.name === 'arg0') || rotateInfo.params[0];
        if (aParam) {
          if (aParam.type === 'vector') {
            const match = aParam.value.match(/\[\s*([^,]+),\s*([^,]+),\s*([^\]]+)\]/);
            if (match) {
              rotation = [parseFloat(match[1]), parseFloat(match[2]), parseFloat(match[3])];
            }
          } else if (aParam.type === 'number') {
            rotation = [0, 0, parseFloat(aParam.value) || 0];
          }
        }
      }

      this.lastRotation = rotation;
      this.transformCallback({ position, rotation, relativeToOrigin, size: scadSize(geoInfo.bbox) });
    };

    // Check if this is a top-level part (show full module chain)
    for (const p of this.lastParts) {
      if (p.from.offset === from.offset && p.to.offset === to.offset) {
        if (p.stmt.type === 'ModuleCallStatement') {
          const chain = this.extractChainParams(p.stmt, code);
          if (this.paramCallback) this.paramCallback(chain.length > 0 ? chain : null);
          showDims(chain);
          fireTransformInfo(chain);
          return;
        }
      }
    }

    // Check if it's a child in a boolean group
    for (const p of this.lastParts) {
      if (p.children) {
        const child = p.children.find(c => c.from.offset === from.offset && c.to.offset === to.offset);
        if (child && child.stmt.type === 'ModuleCallStatement') {
          const chain = this.extractChainParams(child.stmt, code);
          if (this.paramCallback) this.paramCallback(chain.length > 0 ? chain : null);
          showDims(chain);
          fireTransformInfo(chain);
          return;
        }
      }
    }

    // No match found — clear panel and dimensions
    if (this.paramCallback) this.paramCallback(null);
    if (this.transformCallback) this.transformCallback(null);
    this.lastDimChain = null;
    this.viewport.clearDimensions();
  }

  /**
   * Walk down the module call chain (e.g. translate → align → cube)
   * and extract parameters for each module.
   */
  private extractChainParams(stmt: ModuleCallStatement, code: string): ParamInfo[] {
    const chain: ParamInfo[] = [];
    let current: Statement = stmt;

    while (current.type === 'ModuleCallStatement') {
      const mcs = current as ModuleCallStatement;
      const info = this.extractParams(mcs, code);
      chain.push(info);

      // Get non-layout children to continue the chain.
      // If filtering removes all children, the layout module IS the chain continuation
      // (e.g., color() attach() shape()), so fall back to the original children.
      let regularChildren = mcs.children.filter(c =>
        !(c.type === 'ModuleCallStatement' && EditorApp.POSITIONING_MODULES.has(c.name))
      );
      if (regularChildren.length === 0 && mcs.children.length > 0) {
        regularChildren = mcs.children;
      }

      // Continue down the chain if exactly one regular child
      if (regularChildren.length === 1) {
        current = regularChildren[0];
      } else {
        break;
      }
    }

    return chain;
  }

  // Default positional parameter names for known primitives
  private static POSITIONAL_NAMES: Record<string, string[]> = {
    cube: ['size'],
    sphere: ['r'],
    cylinder: ['h', 'r', 'r1', 'r2'],
    circle: ['r'],
    square: ['size'],
    polyhedron: ['points', 'faces'],
    linear_extrude: ['height'],
    rotate_extrude: ['angle'],
    translate: ['v'],
    rotate: ['a'],
    scale: ['v'],
    attach: ['anchor', 'from'],
    align: ['anchor', 'alignment'],
    position: ['anchor'],
    frame: ['size'],
    spur_gear: ['teeth', 'mod'],
    helical_gear: ['teeth', 'mod'],
    herringbone_gear: ['teeth', 'mod'],
    rack: ['teeth', 'mod'],
    bevel_gear: ['teeth', 'mod'],
    worm: ['starts', 'mod'],
    torus: ['r1', 'r2'],
    pipe: ['h', 'r'],
    ellipsoid: ['r'],
    wedge: ['size'],
    capsule: ['h', 'r'],
    rounded_cube: ['size', 'r'],
    regular_polygon: ['r', 'n'],
    hexagon: ['r'],
    slot: ['w', 'h'],
    star: ['points', 'r_outer', 'r_inner'],
    t_profile: ['w', 'h', 't'],
    i_profile: ['w', 'h', 't'],
    thread: ['d', 'pitch'],
    spring: ['r', 'r_wire'],
    bolt: ['size', 'length'],
    nut: ['size', 'h'],
    enclosure: ['size'],
    shell: ['thickness'],
    text: ['text', 'size'],
  };

  /** Anchor constant names recognized in attach/align expressions */
  private static ANCHOR_NAMES = new Set([
    'CENTER', 'TOP', 'BOTTOM', 'LEFT', 'RIGHT', 'FRONT', 'BACK',
  ]);

  /** Axis constant names recognized in attach/align modules */
  private static AXIS_NAMES = new Set(['X', 'Y', 'Z', 'XY', 'XZ', 'YZ']);

  /**
   * Check if an expression is composed entirely of anchor identifiers
   * joined with `+` (e.g., `TOP`, `TOP + RIGHT`, `LEFT + FRONT + TOP`).
   */
  private static isAnchorExpression(expr: Expression): boolean {
    if (expr.type === 'IdentifierExpression') {
      return EditorApp.ANCHOR_NAMES.has(expr.name);
    }
    if (expr.type === 'BinaryExpression' && expr.operator === '+') {
      return EditorApp.isAnchorExpression(expr.left) && EditorApp.isAnchorExpression(expr.right);
    }
    return false;
  }

  private extractParams(stmt: ModuleCallStatement, code: string): ParamInfo {
    const params: ParamField[] = [];
    const defaultNames = EditorApp.POSITIONAL_NAMES[stmt.name] || [];
    const isAnchorModule = stmt.name === 'attach' || stmt.name === 'align' || stmt.name === 'position';

    for (const arg of stmt.args) {
      const name = arg.name ?? (defaultNames[params.length] || `arg${params.length}`);
      const from = arg.value.from.offset;
      const to = arg.value.to.offset;
      const value = code.substring(from, to);

      let type: ParamField['type'] = 'readonly';

      // Detect anchor expressions for anchor/from/alignment params in layout modules
      if (isAnchorModule && (name === 'anchor' || name === 'from' || name === 'alignment') &&
          EditorApp.isAnchorExpression(arg.value)) {
        type = 'anchor';
      } else if (isAnchorModule && name === 'axis' &&
          arg.value.type === 'IdentifierExpression' &&
          EditorApp.AXIS_NAMES.has(arg.value.name)) {
        type = 'axis';
      } else if (arg.value.type === 'NumberLiteral') {
        type = 'number';
      } else if (arg.value.type === 'UnaryExpression' && arg.value.operator === '-' && arg.value.operand.type === 'NumberLiteral') {
        type = 'number';
      } else if (arg.value.type === 'VectorExpression') {
        // Check if all elements are simple literals
        const allLiteral = arg.value.elements.every(el =>
          el.type === 'NumberLiteral' ||
          (el.type === 'UnaryExpression' && el.operator === '-' && el.operand.type === 'NumberLiteral')
        );
        type = allLiteral ? 'vector' : 'readonly';
      } else if (arg.value.type === 'BooleanLiteral') {
        type = 'boolean';
      } else if (arg.value.type === 'StringLiteral') {
        type = 'string';
      }

      params.push({ name, value, from, to, type });
    }

    return { moduleName: stmt.name, params };
  }

  // --- Dimension Computation ---

  /**
   * Generate dimensions for rotated shapes by matching world-space bbox dimensions
   * to SCAD parameters. Returns both dimension lines and draggable handles.
   */
  private computeRotatedCubeDimensions(
    bbox: THREE.Box3,
    sizeParam: ParamField,
    code: string,
    isCentered: boolean,
  ): DimensionSetup {
    const lines: DimensionLine[] = [];
    const handles: DragHandle[] = [];
    const min = bbox.min;
    const max = bbox.max;
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    const diag = size.length();
    const offsetDist = Math.max(0.15, diag * 0.02);
    const scaleFactor = isCentered ? 2 : 1;

    // Parse the SCAD size vector
    const parsed = EditorApp.parseVectorParam(sizeParam, code);
    if (!parsed || parsed.values.length < 3) {
      return this.computeFallbackDimensions(bbox);
    }

    // World dimensions in Three.js: [size.x, size.y, size.z]
    // SCAD dimensions: parsed.values = [sx, sy, sz]
    // Standard mapping (no rotation): X→0, Y→2, Z→1
    // With rotation, we need to find which SCAD index corresponds to each world axis

    // Match world dimensions to SCAD params by finding best value match
    const worldDims = [
      { axis: 'x' as const, size: size.x, vec: new THREE.Vector3(1, 0, 0), dimKey: 'width' },
      { axis: 'y' as const, size: size.y, vec: new THREE.Vector3(0, 1, 0), dimKey: 'height' },
      { axis: 'z' as const, size: size.z, vec: new THREE.Vector3(0, 0, 1), dimKey: 'depth' },
    ];

    // Standard SCAD→Three.js mapping for reference: sx→X, sy→Z, sz→Y
    // So we compare: world X to sx, world Z to sy, world Y to sz
    // But with rotation this changes, so we match by closest value

    const scadValues = [...parsed.values];
    const usedIndices = new Set<number>();
    const axisMapping: Array<{ worldAxis: typeof worldDims[0]; scadIndex: number }> = [];

    // Greedily match each world dimension to the closest unused SCAD value
    for (const wd of worldDims) {
      let bestIdx = -1;
      let bestDiff = Infinity;
      for (let i = 0; i < scadValues.length; i++) {
        if (usedIndices.has(i)) continue;
        const diff = Math.abs(wd.size - scadValues[i]);
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
        }
      }
      if (bestIdx >= 0) {
        usedIndices.add(bestIdx);
        axisMapping.push({ worldAxis: wd, scadIndex: bestIdx });
      }
    }

    // Create dimension lines and handles for each axis
    for (const mapping of axisMapping) {
      const { worldAxis, scadIndex } = mapping;
      const scadValue = scadValues[scadIndex];

      const makeVC = (): VectorContext => ({
        values: parsed.values.slice(),
        editIndex: scadIndex,
        fullFrom: sizeParam.from,
        fullTo: sizeParam.to,
      });

      // Dimension line
      if (worldAxis.axis === 'x') {
        lines.push({
          start: new THREE.Vector3(min.x, min.y, max.z),
          end: new THREE.Vector3(max.x, min.y, max.z),
          offsetDir: new THREE.Vector3(0, 0, 1),
          offsetDist,
          value: scadValue,
          dimKey: worldAxis.dimKey,
          paramFrom: parsed.componentOffsets[scadIndex].from,
          paramTo: parsed.componentOffsets[scadIndex].to,
          vectorContext: makeVC(),
        });
      } else if (worldAxis.axis === 'y') {
        lines.push({
          start: new THREE.Vector3(min.x, min.y, max.z),
          end: new THREE.Vector3(min.x, max.y, max.z),
          offsetDir: new THREE.Vector3(-1, 0, 0),
          offsetDist,
          value: scadValue,
          dimKey: worldAxis.dimKey,
          paramFrom: parsed.componentOffsets[scadIndex].from,
          paramTo: parsed.componentOffsets[scadIndex].to,
          vectorContext: makeVC(),
        });
      } else {
        lines.push({
          start: new THREE.Vector3(max.x, min.y, min.z),
          end: new THREE.Vector3(max.x, min.y, max.z),
          offsetDir: new THREE.Vector3(1, 0, 0),
          offsetDist,
          value: scadValue,
          dimKey: worldAxis.dimKey,
          paramFrom: parsed.componentOffsets[scadIndex].from,
          paramTo: parsed.componentOffsets[scadIndex].to,
          vectorContext: makeVC(),
        });
      }
    }

    // Build axis entries for handles
    const getAxisEntry = (axis: 'x' | 'y' | 'z', sign: number) => {
      const mapping = axisMapping.find(m => m.worldAxis.axis === axis);
      if (!mapping) return null;
      const scadIndex = mapping.scadIndex;
      return {
        axis: mapping.worldAxis.vec.clone(),
        sign,
        scaleFactor,
        value: scadValues[scadIndex],
        paramFrom: parsed.componentOffsets[scadIndex].from,
        paramTo: parsed.componentOffsets[scadIndex].to,
        dimKey: mapping.worldAxis.dimKey,
        vectorContext: {
          values: parsed.values.slice(),
          editIndex: scadIndex,
          fullFrom: sizeParam.from,
          fullTo: sizeParam.to,
        } as VectorContext,
      };
    };

    // 4 corner handles on bottom face (X and Z axes)
    const xMinus = getAxisEntry('x', -1);
    const xPlus = getAxisEntry('x', +1);
    const zMinus = getAxisEntry('z', -1);
    const zPlus = getAxisEntry('z', +1);

    if (xMinus && zMinus) handles.push({ position: new THREE.Vector3(min.x, min.y, min.z), axes: [xMinus, zMinus] });
    if (xPlus && zMinus) handles.push({ position: new THREE.Vector3(max.x, min.y, min.z), axes: [xPlus, zMinus] });
    if (xPlus && zPlus) handles.push({ position: new THREE.Vector3(max.x, min.y, max.z), axes: [xPlus, zPlus] });
    if (xMinus && zPlus) handles.push({ position: new THREE.Vector3(min.x, min.y, max.z), axes: [xMinus, zPlus] });

    // Top-center handle for height (Y axis)
    const yPlus = getAxisEntry('y', +1);
    if (yPlus) {
      handles.push({
        position: new THREE.Vector3(center.x, max.y, center.z),
        axes: [yPlus],
      });
    }

    return { handles, lines, workPlaneY: min.y, bbox: bbox.clone() };
  }

  /**
   * Generate read-only dimension lines from the world-space bounding box.
   * Used when no editable parameters are found.
   */
  private computeFallbackDimensions(bbox: THREE.Box3): DimensionSetup {
    const lines: DimensionLine[] = [];
    const min = bbox.min;
    const max = bbox.max;
    const size = new THREE.Vector3();
    bbox.getSize(size);

    const diag = size.length();
    const offsetDist = Math.max(0.15, diag * 0.02);

    if (size.x > 0.001 && size.y > 0.001 && size.z > 0.001) {
      // Width (X axis)
      lines.push({
        start: new THREE.Vector3(min.x, min.y, max.z),
        end: new THREE.Vector3(max.x, min.y, max.z),
        offsetDir: new THREE.Vector3(0, 0, 1),
        offsetDist,
        value: parseFloat(size.x.toFixed(2)),
        dimKey: 'width',
        paramFrom: 0, paramTo: 0,
      });
      // Depth (Z axis in Three.js = Y in SCAD)
      lines.push({
        start: new THREE.Vector3(max.x, min.y, min.z),
        end: new THREE.Vector3(max.x, min.y, max.z),
        offsetDir: new THREE.Vector3(1, 0, 0),
        offsetDist,
        value: parseFloat(size.z.toFixed(2)),
        dimKey: 'depth',
        paramFrom: 0, paramTo: 0,
      });
      // Height (Y axis in Three.js = Z in SCAD)
      lines.push({
        start: new THREE.Vector3(min.x, min.y, max.z),
        end: new THREE.Vector3(min.x, max.y, max.z),
        offsetDir: new THREE.Vector3(-1, 0, 0),
        offsetDist,
        value: parseFloat(size.y.toFixed(2)),
        dimKey: 'height',
        paramFrom: 0, paramTo: 0,
      });
    }

    return { handles: [], lines, workPlaneY: min.y, bbox: bbox.clone() };
  }

  /**
   * Parse a numeric value from a ParamField (handles negative numbers).
   */
  private static parseParamValue(field: ParamField): number | null {
    const v = parseFloat(field.value);
    return isNaN(v) ? null : v;
  }

  /**
   * Parse a vector ParamField like "[10, 20, 5]" into component values and offsets.
   */
  private static parseVectorParam(field: ParamField, code: string): { values: number[]; componentOffsets: Array<{ from: number; to: number }> } | null {
    // Allow both 'vector' type and 'readonly' type that looks like a vector
    if (field.type !== 'vector' && !(field.type === 'readonly' && field.value.startsWith('['))) return null;
    // Re-parse the slice to get element offsets
    const text = code.substring(field.from, field.to);
    // Simple approach: split on commas, tracking offsets
    const values: number[] = [];
    const componentOffsets: Array<{ from: number; to: number }> = [];
    let depth = 0;
    let start = -1;
    for (let i = 0; i < text.length; i++) {
      const ch = text[i];
      if (ch === '[') {
        depth++;
        if (depth === 1) start = i + 1;
      } else if (ch === ']') {
        if (depth === 1 && start >= 0) {
          const comp = text.substring(start, i).trim();
          const v = parseFloat(comp);
          if (!isNaN(v)) {
            // Find actual start/end within the component (skip whitespace)
            const compStart = text.indexOf(comp, start);
            values.push(v);
            componentOffsets.push({
              from: field.from + compStart,
              to: field.from + compStart + comp.length,
            });
          }
        }
        depth--;
      } else if (ch === ',' && depth === 1) {
        if (start >= 0) {
          const comp = text.substring(start, i).trim();
          const v = parseFloat(comp);
          if (!isNaN(v)) {
            const compStart = text.indexOf(comp, start);
            values.push(v);
            componentOffsets.push({
              from: field.from + compStart,
              to: field.from + compStart + comp.length,
            });
          }
        }
        start = i + 1;
      }
    }
    return values.length > 0 ? { values, componentOffsets } : null;
  }

  /**
   * Map ParamInfo chain + world-space bounding box to DimensionSetup.
   *
   * Coordinate convention:
   *   SCAD Z-up → Three.js Y-up (via rotateX(-PI/2) in showParts)
   *   SCAD [sx, sy, sz] → Three.js width=sx (X), height=sz (Y), depth=sy (Z)
   */
  private computeDimensions(chain: ParamInfo[], bbox: THREE.Box3, _center: THREE.Vector3): DimensionSetup {
    const lines: DimensionLine[] = [];
    const handles: DragHandle[] = [];
    const code = this.editor.getCode();

    const empty: DimensionSetup = { handles: [], lines: [], workPlaneY: bbox.min.y };

    // Check if there's a rotation transform in the chain.
    const hasRotation = chain.some(info => info.moduleName === 'rotate');

    // Find the innermost shape module (skip transforms like translate/rotate)
    const SHAPE_MODULES = [
      'cube', 'sphere', 'cylinder',
      'spur_gear', 'helical_gear', 'herringbone_gear', 'rack', 'bevel_gear', 'worm',
    ];
    const shapeModule = chain.find(info =>
      SHAPE_MODULES.includes(info.moduleName)
    );

    // If rotation is present, use smart axis matching for cubes
    if (hasRotation && shapeModule?.moduleName === 'cube') {
      const sizeParam = shapeModule.params.find(p => p.name === 'size');
      if (sizeParam && (sizeParam.type === 'vector' || (sizeParam.type === 'readonly' && sizeParam.value.startsWith('[')))) {
        const isCentered = shapeModule.params.find(p => p.name === 'center')?.value === 'true';
        return this.computeRotatedCubeDimensions(bbox, sizeParam, code, isCentered);
      }
      return this.computeFallbackDimensions(bbox);
    } else if (hasRotation) {
      // For other rotated shapes (sphere, cylinder), use fallback for now
      return this.computeFallbackDimensions(bbox);
    }

    const min = bbox.min;
    const max = bbox.max;
    const size = new THREE.Vector3();
    bbox.getSize(size);
    const center = new THREE.Vector3();
    bbox.getCenter(center);

    // Default offset distance proportional to shape size
    const diag = size.length();
    const offsetDist = Math.max(0.15, diag * 0.02);

    // Detect center=true from params
    const isCentered = shapeModule?.params.find(p => p.name === 'center')?.value === 'true';
    const scaleFactor = isCentered ? 2 : 1;

    if (shapeModule) switch (shapeModule.moduleName) {
      case 'cube': {
        const sizeParam = shapeModule.params.find(p => p.name === 'size');
        if (!sizeParam) break;

        if (sizeParam.type === 'vector') {
          const parsed = EditorApp.parseVectorParam(sizeParam, code);
          if (!parsed || parsed.values.length < 3) break;

          const [sx, sy, sz] = parsed.values;

          const makeVC = (editIndex: number): VectorContext => ({
            values: parsed.values.slice(),
            editIndex,
            fullFrom: sizeParam.from,
            fullTo: sizeParam.to,
          });

          // Dimension lines — width and depth project outward on the ground plane
          lines.push({
            start: new THREE.Vector3(min.x, min.y, max.z),
            end: new THREE.Vector3(max.x, min.y, max.z),
            offsetDir: new THREE.Vector3(0, 0, 1),
            offsetDist,
            value: sx,
            dimKey: 'width',
            paramFrom: parsed.componentOffsets[0].from,
            paramTo: parsed.componentOffsets[0].to,
            vectorContext: makeVC(0),
          });
          lines.push({
            start: new THREE.Vector3(max.x, min.y, min.z),
            end: new THREE.Vector3(max.x, min.y, max.z),
            offsetDir: new THREE.Vector3(1, 0, 0),
            offsetDist,
            value: sy,
            dimKey: 'depth',
            paramFrom: parsed.componentOffsets[1].from,
            paramTo: parsed.componentOffsets[1].to,
            vectorContext: makeVC(1),
          });
          lines.push({
            start: new THREE.Vector3(min.x, min.y, max.z),
            end: new THREE.Vector3(min.x, max.y, max.z),
            offsetDir: new THREE.Vector3(-1, 0, 0),
            offsetDist,
            value: sz,
            dimKey: 'height',
            paramFrom: parsed.componentOffsets[2].from,
            paramTo: parsed.componentOffsets[2].to,
            vectorContext: makeVC(2),
          });

          // Helper to build axis entry for width (X axis in Three.js)
          const widthAxis = (sign: number) => ({
            axis: new THREE.Vector3(1, 0, 0),
            sign,
            scaleFactor,
            value: sx,
            paramFrom: parsed.componentOffsets[0].from,
            paramTo: parsed.componentOffsets[0].to,
            dimKey: 'width',
            vectorContext: makeVC(0),
          });

          // Helper to build axis entry for depth (Z axis in Three.js)
          const depthAxis = (sign: number) => ({
            axis: new THREE.Vector3(0, 0, 1),
            sign,
            scaleFactor,
            value: sy,
            paramFrom: parsed.componentOffsets[1].from,
            paramTo: parsed.componentOffsets[1].to,
            dimKey: 'depth',
            vectorContext: makeVC(1),
          });

          // 4 corner handles on bottom face — each has width + depth axes
          handles.push({ position: new THREE.Vector3(min.x, min.y, min.z), axes: [widthAxis(-1), depthAxis(-1)] });
          handles.push({ position: new THREE.Vector3(max.x, min.y, min.z), axes: [widthAxis(+1), depthAxis(-1)] });
          handles.push({ position: new THREE.Vector3(max.x, min.y, max.z), axes: [widthAxis(+1), depthAxis(+1)] });
          handles.push({ position: new THREE.Vector3(min.x, min.y, max.z), axes: [widthAxis(-1), depthAxis(+1)] });

          // Top-center handle for height (Y axis in Three.js)
          handles.push({
            position: new THREE.Vector3(center.x, max.y, center.z),
            axes: [{
              axis: new THREE.Vector3(0, 1, 0),
              sign: +1,
              scaleFactor,
              value: sz,
              paramFrom: parsed.componentOffsets[2].from,
              paramTo: parsed.componentOffsets[2].to,
              dimKey: 'height',
              vectorContext: makeVC(2),
            }],
          });

        } else if (sizeParam.type === 'readonly' && sizeParam.value.startsWith('[')) {
          // Expression-based vector (e.g. [var, var+1, expr]) — use bbox-derived
          // values so dimensions are visible and handles replace the whole vector
          // with concrete numbers on drag.
          const sx = parseFloat(size.x.toFixed(2));
          const sy = parseFloat(size.z.toFixed(2)); // SCAD Y → Three.js Z
          const sz = parseFloat(size.y.toFixed(2)); // SCAD Z → Three.js Y

          const makeVC = (editIndex: number): VectorContext => ({
            values: [sx, sy, sz],
            editIndex,
            fullFrom: sizeParam.from,
            fullTo: sizeParam.to,
          });

          lines.push({ start: new THREE.Vector3(min.x, min.y, max.z), end: new THREE.Vector3(max.x, min.y, max.z), offsetDir: new THREE.Vector3(0, 0, 1), offsetDist, value: sx, dimKey: 'width', paramFrom: sizeParam.from, paramTo: sizeParam.to, vectorContext: makeVC(0) });
          lines.push({ start: new THREE.Vector3(max.x, min.y, min.z), end: new THREE.Vector3(max.x, min.y, max.z), offsetDir: new THREE.Vector3(1, 0, 0), offsetDist, value: sy, dimKey: 'depth', paramFrom: sizeParam.from, paramTo: sizeParam.to, vectorContext: makeVC(1) });
          lines.push({ start: new THREE.Vector3(min.x, min.y, max.z), end: new THREE.Vector3(min.x, max.y, max.z), offsetDir: new THREE.Vector3(-1, 0, 0), offsetDist, value: sz, dimKey: 'height', paramFrom: sizeParam.from, paramTo: sizeParam.to, vectorContext: makeVC(2) });

          const widthAxis = (sign: number) => ({ axis: new THREE.Vector3(1, 0, 0), sign, scaleFactor, value: sx, paramFrom: sizeParam.from, paramTo: sizeParam.to, dimKey: 'width', vectorContext: makeVC(0) });
          const depthAxis = (sign: number) => ({ axis: new THREE.Vector3(0, 0, 1), sign, scaleFactor, value: sy, paramFrom: sizeParam.from, paramTo: sizeParam.to, dimKey: 'depth', vectorContext: makeVC(1) });

          handles.push({ position: new THREE.Vector3(min.x, min.y, min.z), axes: [widthAxis(-1), depthAxis(-1)] });
          handles.push({ position: new THREE.Vector3(max.x, min.y, min.z), axes: [widthAxis(+1), depthAxis(-1)] });
          handles.push({ position: new THREE.Vector3(max.x, min.y, max.z), axes: [widthAxis(+1), depthAxis(+1)] });
          handles.push({ position: new THREE.Vector3(min.x, min.y, max.z), axes: [widthAxis(-1), depthAxis(+1)] });
          handles.push({
            position: new THREE.Vector3(center.x, max.y, center.z),
            axes: [{ axis: new THREE.Vector3(0, 1, 0), sign: +1, scaleFactor, value: sz, paramFrom: sizeParam.from, paramTo: sizeParam.to, dimKey: 'height', vectorContext: makeVC(2) }],
          });

        } else if (sizeParam.type === 'number') {
          const val = EditorApp.parseParamValue(sizeParam);
          if (val === null) break;

          lines.push({ start: new THREE.Vector3(min.x, min.y, max.z), end: new THREE.Vector3(max.x, min.y, max.z), offsetDir: new THREE.Vector3(0, 0, 1), offsetDist, value: val, dimKey: 'width', paramFrom: sizeParam.from, paramTo: sizeParam.to });
          lines.push({ start: new THREE.Vector3(max.x, min.y, min.z), end: new THREE.Vector3(max.x, min.y, max.z), offsetDir: new THREE.Vector3(1, 0, 0), offsetDist, value: val, dimKey: 'depth', paramFrom: sizeParam.from, paramTo: sizeParam.to });
          lines.push({ start: new THREE.Vector3(min.x, min.y, max.z), end: new THREE.Vector3(min.x, max.y, max.z), offsetDir: new THREE.Vector3(-1, 0, 0), offsetDist, value: val, dimKey: 'height', paramFrom: sizeParam.from, paramTo: sizeParam.to });

          // Scalar cube: all axes reference the same param
          const scalarAxis = (dir: THREE.Vector3, sign: number, dimKey: string) => ({
            axis: dir,
            sign,
            scaleFactor,
            value: val,
            paramFrom: sizeParam.from,
            paramTo: sizeParam.to,
            dimKey,
          });

          handles.push({ position: new THREE.Vector3(min.x, min.y, min.z), axes: [scalarAxis(new THREE.Vector3(1, 0, 0), -1, 'width'), scalarAxis(new THREE.Vector3(0, 0, 1), -1, 'depth')] });
          handles.push({ position: new THREE.Vector3(max.x, min.y, min.z), axes: [scalarAxis(new THREE.Vector3(1, 0, 0), +1, 'width'), scalarAxis(new THREE.Vector3(0, 0, 1), -1, 'depth')] });
          handles.push({ position: new THREE.Vector3(max.x, min.y, max.z), axes: [scalarAxis(new THREE.Vector3(1, 0, 0), +1, 'width'), scalarAxis(new THREE.Vector3(0, 0, 1), +1, 'depth')] });
          handles.push({ position: new THREE.Vector3(min.x, min.y, max.z), axes: [scalarAxis(new THREE.Vector3(1, 0, 0), -1, 'width'), scalarAxis(new THREE.Vector3(0, 0, 1), +1, 'depth')] });

          // Top-center handle for height
          handles.push({
            position: new THREE.Vector3(center.x, max.y, center.z),
            axes: [scalarAxis(new THREE.Vector3(0, 1, 0), +1, 'height')],
          });
        }
        break;
      }

      case 'sphere': {
        const rParam = shapeModule.params.find(p => p.name === 'r');
        const dParam = shapeModule.params.find(p => p.name === 'd');

        // Sphere always centered, scaleFactor = 2
        const sphereScale = 2;
        const sphereLineOffset = offsetDist + size.y * 0.35;

        if (rParam) {
          const rVal = EditorApp.parseParamValue(rParam) ?? parseFloat((size.x / 2).toFixed(2));
          // Line from center to edge — length matches radius value
          lines.push({ start: new THREE.Vector3(center.x, center.y, center.z), end: new THREE.Vector3(max.x, center.y, center.z), offsetDir: new THREE.Vector3(0, -1, 0), offsetDist: sphereLineOffset, value: rVal, dimKey: 'radius', paramFrom: rParam.from, paramTo: rParam.to });

          handles.push({ position: new THREE.Vector3(max.x, center.y, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: +1, scaleFactor: sphereScale, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(min.x, center.y, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: -1, scaleFactor: sphereScale, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, center.y, max.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: +1, scaleFactor: sphereScale, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, center.y, min.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: -1, scaleFactor: sphereScale, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
        } else if (dParam) {
          const dVal = EditorApp.parseParamValue(dParam) ?? parseFloat(size.x.toFixed(2));
          // Line from edge to edge — length matches diameter value
          lines.push({ start: new THREE.Vector3(min.x, center.y, center.z), end: new THREE.Vector3(max.x, center.y, center.z), offsetDir: new THREE.Vector3(0, -1, 0), offsetDist: sphereLineOffset, value: dVal, dimKey: 'diameter', paramFrom: dParam.from, paramTo: dParam.to });

          handles.push({ position: new THREE.Vector3(max.x, center.y, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: +1, scaleFactor: sphereScale, value: dVal, paramFrom: dParam.from, paramTo: dParam.to, dimKey: 'diameter', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(min.x, center.y, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: -1, scaleFactor: sphereScale, value: dVal, paramFrom: dParam.from, paramTo: dParam.to, dimKey: 'diameter', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, center.y, max.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: +1, scaleFactor: sphereScale, value: dVal, paramFrom: dParam.from, paramTo: dParam.to, dimKey: 'diameter', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, center.y, min.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: -1, scaleFactor: sphereScale, value: dVal, paramFrom: dParam.from, paramTo: dParam.to, dimKey: 'diameter', isCenteredParam: true }] });
        }
        break;
      }

      case 'cylinder': {
        const hParam = shapeModule.params.find(p => p.name === 'h');
        const rParam = shapeModule.params.find(p => p.name === 'r');
        const r1Param = shapeModule.params.find(p => p.name === 'r1');
        const r2Param = shapeModule.params.find(p => p.name === 'r2');
        const dParam = shapeModule.params.find(p => p.name === 'd');
        const d1Param = shapeModule.params.find(p => p.name === 'd1');
        const d2Param = shapeModule.params.find(p => p.name === 'd2');
        const cylCentered = shapeModule.params.find(p => p.name === 'center')?.value === 'true';
        const cylHeightScale = cylCentered ? 2 : 1;

        // Detect cylinder orientation from bbox shape.
        // A cylinder has two equal radius axes and one height axis.
        // For a parent rotate(), the height axis may no longer be Y.
        type CylAxis = { comp: 'x' | 'y' | 'z'; vec: THREE.Vector3; sz: number };
        const cylAxes: CylAxis[] = [
          { comp: 'x', vec: new THREE.Vector3(1, 0, 0), sz: size.x },
          { comp: 'y', vec: new THREE.Vector3(0, 1, 0), sz: size.y },
          { comp: 'z', vec: new THREE.Vector3(0, 0, 1), sz: size.z },
        ];
        // Find the two most similar sizes — those are the radius axes.
        const diffs = [
          { pair: [0, 1] as const, diff: Math.abs(cylAxes[0].sz - cylAxes[1].sz) },
          { pair: [0, 2] as const, diff: Math.abs(cylAxes[0].sz - cylAxes[2].sz) },
          { pair: [1, 2] as const, diff: Math.abs(cylAxes[1].sz - cylAxes[2].sz) },
        ];
        diffs.sort((a, b) => a.diff - b.diff);
        const radiusPair = diffs[0].pair;
        let hIdx = [0, 1, 2].find(i => i !== radiusPair[0] && i !== radiusPair[1])!;
        // If all three sizes are very similar (h ≈ diameter), default to Y
        if (diffs[0].diff > diffs[2].diff * 0.8) hIdx = 1;

        const hA = cylAxes[hIdx];                                        // height axis
        const rAxes = cylAxes.filter((_, i) => i !== hIdx);
        const rA = rAxes[0];                                             // primary radius axis (for dim lines)
        const rB = rAxes[1];                                             // secondary radius axis

        // Shorthand: build a point from axis-relative coordinates
        const cylPt = (hv: number, rv: number, rv2: number) => {
          const p = new THREE.Vector3();
          p[hA.comp] = hv; p[rA.comp] = rv; p[rB.comp] = rv2;
          return p;
        };
        const hMin = min[hA.comp], hMax = max[hA.comp], hCtr = center[hA.comp];
        const rMin = min[rA.comp], rMax = max[rA.comp], rCtr = center[rA.comp];
        const rBCtr = center[rB.comp], rBMin = min[rB.comp], rBMax = max[rB.comp];

        // Height
        if (hParam) {
          const hVal = EditorApp.parseParamValue(hParam) ?? parseFloat(hA.sz.toFixed(2));
          lines.push({
            start: cylPt(hMin, rMin, rBCtr), end: cylPt(hMax, rMin, rBCtr),
            offsetDir: rA.vec.clone().negate(), offsetDist,
            value: hVal, dimKey: 'height', paramFrom: hParam.from, paramTo: hParam.to,
          });
          handles.push({
            position: cylPt(hMax, rCtr, rBCtr),
            axes: [{ axis: hA.vec.clone(), sign: +1, scaleFactor: cylHeightScale, value: hVal, paramFrom: hParam.from, paramTo: hParam.to, dimKey: 'height', isCenteredParam: cylCentered }],
          });
        }

        // Helper to add radius/diameter handles along both radius axes at a height-axis position
        const addRadHandles = (hv: number, val: number, from: number, to: number, key: string) => {
          handles.push({ position: cylPt(hv, rMax, rBCtr), axes: [{ axis: rA.vec.clone(), sign: +1, scaleFactor: 2, value: val, paramFrom: from, paramTo: to, dimKey: key, isCenteredParam: true }] });
          handles.push({ position: cylPt(hv, rMin, rBCtr), axes: [{ axis: rA.vec.clone(), sign: -1, scaleFactor: 2, value: val, paramFrom: from, paramTo: to, dimKey: key, isCenteredParam: true }] });
          handles.push({ position: cylPt(hv, rCtr, rBMax), axes: [{ axis: rB.vec.clone(), sign: +1, scaleFactor: 2, value: val, paramFrom: from, paramTo: to, dimKey: key, isCenteredParam: true }] });
          handles.push({ position: cylPt(hv, rCtr, rBMin), axes: [{ axis: rB.vec.clone(), sign: -1, scaleFactor: 2, value: val, paramFrom: from, paramTo: to, dimKey: key, isCenteredParam: true }] });
        };

        // Radius — prefer r params, fall back to d params
        if (rParam) {
          const rVal = EditorApp.parseParamValue(rParam) ?? parseFloat((rA.sz / 2).toFixed(2));
          lines.push({
            start: cylPt(hMin, rCtr, rBCtr), end: cylPt(hMin, rMax, rBCtr),
            offsetDir: hA.vec.clone().negate(), offsetDist,
            value: rVal, dimKey: 'radius', paramFrom: rParam.from, paramTo: rParam.to,
          });
          addRadHandles(hMin, rVal, rParam.from, rParam.to, 'radius');
        } else if (!r1Param && !r2Param && !d1Param && !d2Param && dParam) {
          const dVal = EditorApp.parseParamValue(dParam) ?? parseFloat(rA.sz.toFixed(2));
          lines.push({
            start: cylPt(hMin, rMin, rBCtr), end: cylPt(hMin, rMax, rBCtr),
            offsetDir: hA.vec.clone().negate(), offsetDist,
            value: dVal, dimKey: 'diameter', paramFrom: dParam.from, paramTo: dParam.to,
          });
          addRadHandles(hMin, dVal, dParam.from, dParam.to, 'diameter');
        }

        // r1/d1 at bottom (hMin)
        if (r1Param) {
          const r1Val = EditorApp.parseParamValue(r1Param) ?? parseFloat((rA.sz / 2).toFixed(2));
          lines.push({
            start: cylPt(hMin, rCtr, rBCtr), end: cylPt(hMin, rMax, rBCtr),
            offsetDir: hA.vec.clone().negate(), offsetDist,
            value: r1Val, dimKey: 'r1', paramFrom: r1Param.from, paramTo: r1Param.to,
          });
          handles.push({ position: cylPt(hMin, rMax, rBCtr), axes: [{ axis: rA.vec.clone(), sign: +1, scaleFactor: 2, value: r1Val, paramFrom: r1Param.from, paramTo: r1Param.to, dimKey: 'r1', isCenteredParam: true }] });
          handles.push({ position: cylPt(hMin, rMin, rBCtr), axes: [{ axis: rA.vec.clone(), sign: -1, scaleFactor: 2, value: r1Val, paramFrom: r1Param.from, paramTo: r1Param.to, dimKey: 'r1', isCenteredParam: true }] });
        } else if (d1Param) {
          const d1Val = EditorApp.parseParamValue(d1Param) ?? parseFloat(rA.sz.toFixed(2));
          lines.push({
            start: cylPt(hMin, rMin, rBCtr), end: cylPt(hMin, rMax, rBCtr),
            offsetDir: hA.vec.clone().negate(), offsetDist,
            value: d1Val, dimKey: 'd1', paramFrom: d1Param.from, paramTo: d1Param.to,
          });
          handles.push({ position: cylPt(hMin, rMax, rBCtr), axes: [{ axis: rA.vec.clone(), sign: +1, scaleFactor: 2, value: d1Val, paramFrom: d1Param.from, paramTo: d1Param.to, dimKey: 'd1', isCenteredParam: true }] });
          handles.push({ position: cylPt(hMin, rMin, rBCtr), axes: [{ axis: rA.vec.clone(), sign: -1, scaleFactor: 2, value: d1Val, paramFrom: d1Param.from, paramTo: d1Param.to, dimKey: 'd1', isCenteredParam: true }] });
        }

        // r2/d2 at top (hMax)
        if (r2Param) {
          const r2Val = EditorApp.parseParamValue(r2Param) ?? parseFloat((rA.sz / 2).toFixed(2));
          lines.push({
            start: cylPt(hMax, rCtr, rBCtr), end: cylPt(hMax, rCtr + r2Val, rBCtr),
            offsetDir: hA.vec.clone(), offsetDist,
            value: r2Val, dimKey: 'r2', paramFrom: r2Param.from, paramTo: r2Param.to,
          });
          handles.push({ position: cylPt(hMax, rCtr + r2Val, rBCtr), axes: [{ axis: rA.vec.clone(), sign: +1, scaleFactor: 2, value: r2Val, paramFrom: r2Param.from, paramTo: r2Param.to, dimKey: 'r2', isCenteredParam: true }] });
          handles.push({ position: cylPt(hMax, rCtr - r2Val, rBCtr), axes: [{ axis: rA.vec.clone(), sign: -1, scaleFactor: 2, value: r2Val, paramFrom: r2Param.from, paramTo: r2Param.to, dimKey: 'r2', isCenteredParam: true }] });
        } else if (d2Param) {
          const d2Val = EditorApp.parseParamValue(d2Param) ?? parseFloat(rA.sz.toFixed(2));
          const d2Radius = d2Val / 2;
          lines.push({
            start: cylPt(hMax, rCtr - d2Radius, rBCtr), end: cylPt(hMax, rCtr + d2Radius, rBCtr),
            offsetDir: hA.vec.clone(), offsetDist,
            value: d2Val, dimKey: 'd2', paramFrom: d2Param.from, paramTo: d2Param.to,
          });
          handles.push({ position: cylPt(hMax, rCtr + d2Radius, rBCtr), axes: [{ axis: rA.vec.clone(), sign: +1, scaleFactor: 2, value: d2Val, paramFrom: d2Param.from, paramTo: d2Param.to, dimKey: 'd2', isCenteredParam: true }] });
          handles.push({ position: cylPt(hMax, rCtr - d2Radius, rBCtr), axes: [{ axis: rA.vec.clone(), sign: -1, scaleFactor: 2, value: d2Val, paramFrom: d2Param.from, paramTo: d2Param.to, dimKey: 'd2', isCenteredParam: true }] });
        }
        break;
      }

      // --- Gear modules (cylinder-like: radial cross-section + height) ---
      case 'spur_gear':
      case 'helical_gear':
      case 'herringbone_gear':
      case 'bevel_gear': {
        const modParam = shapeModule.params.find(p => p.name === 'mod');
        const teethParam = shapeModule.params.find(p => p.name === 'teeth');
        const widthParam = shapeModule.params.find(p => p.name === 'width');
        const boreParam = shapeModule.params.find(p => p.name === 'bore');
        const gearCentered = shapeModule.params.find(p => p.name === 'center')?.value === 'true';

        // Height (width param = face width, mapped to Y axis in Three.js)
        if (widthParam && widthParam.type === 'number') {
          const wVal = EditorApp.parseParamValue(widthParam) ?? parseFloat(size.y.toFixed(2));
          lines.push({
            start: new THREE.Vector3(min.x, min.y, center.z),
            end: new THREE.Vector3(min.x, max.y, center.z),
            offsetDir: new THREE.Vector3(-1, 0, 0), offsetDist,
            value: wVal, dimKey: 'width', paramFrom: widthParam.from, paramTo: widthParam.to,
          });
          handles.push({
            position: new THREE.Vector3(center.x, max.y, center.z),
            axes: [{ axis: new THREE.Vector3(0, 1, 0), sign: +1, scaleFactor: gearCentered ? 2 : 1, value: wVal, paramFrom: widthParam.from, paramTo: widthParam.to, dimKey: 'width', isCenteredParam: gearCentered }],
          });
        }

        // Module (mod) — controls outer radius; outer_diameter = mod * (teeth + 2)
        // Ratio-based drag: newMod = oldMod * (newRadius / oldRadius) works because outerR ∝ mod
        if (modParam && modParam.type === 'number') {
          const modVal = EditorApp.parseParamValue(modParam) ?? 1;
          const outerR = size.x / 2; // bbox half-width = outer radius
          lines.push({
            start: new THREE.Vector3(center.x, min.y, center.z),
            end: new THREE.Vector3(max.x, min.y, center.z),
            offsetDir: new THREE.Vector3(0, -1, 0), offsetDist: offsetDist + size.y * 0.1,
            value: modVal, dimKey: 'mod', paramFrom: modParam.from, paramTo: modParam.to,
          });
          // Radial handles at 4 cardinal points on the bottom face
          const hv = min.y;
          handles.push({ position: new THREE.Vector3(max.x, hv, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: +1, scaleFactor: 2, value: modVal, paramFrom: modParam.from, paramTo: modParam.to, dimKey: 'mod', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(min.x, hv, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: -1, scaleFactor: 2, value: modVal, paramFrom: modParam.from, paramTo: modParam.to, dimKey: 'mod', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, hv, max.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: +1, scaleFactor: 2, value: modVal, paramFrom: modParam.from, paramTo: modParam.to, dimKey: 'mod', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, hv, min.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: -1, scaleFactor: 2, value: modVal, paramFrom: modParam.from, paramTo: modParam.to, dimKey: 'mod', isCenteredParam: true }] });
        }

        // Teeth — display-only dimension line showing tooth count (not draggable)
        if (teethParam && teethParam.type === 'number') {
          const teethVal = EditorApp.parseParamValue(teethParam) ?? 12;
          lines.push({
            start: new THREE.Vector3(center.x, min.y, center.z),
            end: new THREE.Vector3(max.x, min.y, center.z),
            offsetDir: new THREE.Vector3(0, -1, 0), offsetDist: offsetDist + size.y * 0.35,
            value: teethVal, dimKey: 'teeth', paramFrom: teethParam.from, paramTo: teethParam.to,
          });
        }

        // Bore — show as inner radius dimension if present
        if (boreParam && boreParam.type === 'number') {
          const boreVal = EditorApp.parseParamValue(boreParam) ?? 0;
          if (boreVal > 0) {
            const boreR = boreVal / 2;
            lines.push({
              start: new THREE.Vector3(center.x, max.y, center.z),
              end: new THREE.Vector3(center.x + boreR, max.y, center.z),
              offsetDir: new THREE.Vector3(0, 1, 0), offsetDist: offsetDist,
              value: boreVal, dimKey: 'bore', paramFrom: boreParam.from, paramTo: boreParam.to,
            });
            // Bore handles at top face (visible near the hole)
            handles.push({ position: new THREE.Vector3(center.x + boreR, max.y, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: +1, scaleFactor: 2, value: boreVal, paramFrom: boreParam.from, paramTo: boreParam.to, dimKey: 'bore', isCenteredParam: true }] });
            handles.push({ position: new THREE.Vector3(center.x - boreR, max.y, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: -1, scaleFactor: 2, value: boreVal, paramFrom: boreParam.from, paramTo: boreParam.to, dimKey: 'bore', isCenteredParam: true }] });
          }
        }
        break;
      }

      case 'rack': {
        const modParam = shapeModule.params.find(p => p.name === 'mod');
        const teethParam = shapeModule.params.find(p => p.name === 'teeth');
        const widthParam = shapeModule.params.find(p => p.name === 'width');
        const heightParam = shapeModule.params.find(p => p.name === 'height');
        const rackCentered = shapeModule.params.find(p => p.name === 'center')?.value === 'true';

        // Width (depth in Z, the "thickness" of the rack)
        if (widthParam && widthParam.type === 'number') {
          const wVal = EditorApp.parseParamValue(widthParam) ?? parseFloat(size.y.toFixed(2));
          lines.push({
            start: new THREE.Vector3(min.x, min.y, min.z),
            end: new THREE.Vector3(min.x, max.y, min.z),
            offsetDir: new THREE.Vector3(-1, 0, -1).normalize(), offsetDist,
            value: wVal, dimKey: 'width', paramFrom: widthParam.from, paramTo: widthParam.to,
          });
          handles.push({
            position: new THREE.Vector3(center.x, max.y, center.z),
            axes: [{ axis: new THREE.Vector3(0, 1, 0), sign: +1, scaleFactor: rackCentered ? 2 : 1, value: wVal, paramFrom: widthParam.from, paramTo: widthParam.to, dimKey: 'width', isCenteredParam: rackCentered }],
          });
        }

        // Height (bar height, Y direction in SCAD → Z in Three.js)
        if (heightParam && heightParam.type === 'number') {
          const hVal = EditorApp.parseParamValue(heightParam) ?? parseFloat(size.z.toFixed(2));
          lines.push({
            start: new THREE.Vector3(max.x, min.y, min.z),
            end: new THREE.Vector3(max.x, min.y, max.z),
            offsetDir: new THREE.Vector3(1, 0, 0), offsetDist,
            value: hVal, dimKey: 'height', paramFrom: heightParam.from, paramTo: heightParam.to,
          });
          handles.push({
            position: new THREE.Vector3(center.x, min.y, max.z),
            axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: +1, scaleFactor: rackCentered ? 2 : 1, value: hVal, paramFrom: heightParam.from, paramTo: heightParam.to, dimKey: 'height', isCenteredParam: rackCentered }],
          });
        }

        // Length (X axis) — show teeth count dimension
        if (teethParam && teethParam.type === 'number') {
          const teethVal = EditorApp.parseParamValue(teethParam) ?? 10;
          lines.push({
            start: new THREE.Vector3(min.x, min.y, min.z),
            end: new THREE.Vector3(max.x, min.y, min.z),
            offsetDir: new THREE.Vector3(0, 0, -1), offsetDist,
            value: teethVal, dimKey: 'teeth', paramFrom: teethParam.from, paramTo: teethParam.to,
          });
        }
        break;
      }

      case 'worm': {
        const modParam = shapeModule.params.find(p => p.name === 'mod');
        const lengthParam = shapeModule.params.find(p => p.name === 'length');
        const rParam = shapeModule.params.find(p => p.name === 'r');
        const dParam = shapeModule.params.find(p => p.name === 'd');
        const boreParam = shapeModule.params.find(p => p.name === 'bore');
        const wormCentered = shapeModule.params.find(p => p.name === 'center')?.value === 'true';

        // Length (height along Y in Three.js)
        if (lengthParam && lengthParam.type === 'number') {
          const lVal = EditorApp.parseParamValue(lengthParam) ?? parseFloat(size.y.toFixed(2));
          lines.push({
            start: new THREE.Vector3(min.x, min.y, center.z),
            end: new THREE.Vector3(min.x, max.y, center.z),
            offsetDir: new THREE.Vector3(-1, 0, 0), offsetDist,
            value: lVal, dimKey: 'length', paramFrom: lengthParam.from, paramTo: lengthParam.to,
          });
          handles.push({
            position: new THREE.Vector3(center.x, max.y, center.z),
            axes: [{ axis: new THREE.Vector3(0, 1, 0), sign: +1, scaleFactor: wormCentered ? 2 : 1, value: lVal, paramFrom: lengthParam.from, paramTo: lengthParam.to, dimKey: 'length', isCenteredParam: wormCentered }],
          });
        }

        // Radius — prefer r, fall back to d
        if (rParam && rParam.type === 'number') {
          const rVal = EditorApp.parseParamValue(rParam) ?? parseFloat((size.x / 2).toFixed(2));
          lines.push({
            start: new THREE.Vector3(center.x, min.y, center.z),
            end: new THREE.Vector3(max.x, min.y, center.z),
            offsetDir: new THREE.Vector3(0, -1, 0), offsetDist: offsetDist + size.y * 0.1,
            value: rVal, dimKey: 'radius', paramFrom: rParam.from, paramTo: rParam.to,
          });
          const hv = min.y;
          handles.push({ position: new THREE.Vector3(max.x, hv, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: +1, scaleFactor: 2, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(min.x, hv, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: -1, scaleFactor: 2, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, hv, max.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: +1, scaleFactor: 2, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(center.x, hv, min.z), axes: [{ axis: new THREE.Vector3(0, 0, 1), sign: -1, scaleFactor: 2, value: rVal, paramFrom: rParam.from, paramTo: rParam.to, dimKey: 'radius', isCenteredParam: true }] });
        } else if (dParam && dParam.type === 'number') {
          const dVal = EditorApp.parseParamValue(dParam) ?? parseFloat(size.x.toFixed(2));
          lines.push({
            start: new THREE.Vector3(min.x, min.y, center.z),
            end: new THREE.Vector3(max.x, min.y, center.z),
            offsetDir: new THREE.Vector3(0, -1, 0), offsetDist: offsetDist + size.y * 0.1,
            value: dVal, dimKey: 'diameter', paramFrom: dParam.from, paramTo: dParam.to,
          });
          const hv = min.y;
          handles.push({ position: new THREE.Vector3(max.x, hv, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: +1, scaleFactor: 2, value: dVal, paramFrom: dParam.from, paramTo: dParam.to, dimKey: 'diameter', isCenteredParam: true }] });
          handles.push({ position: new THREE.Vector3(min.x, hv, center.z), axes: [{ axis: new THREE.Vector3(1, 0, 0), sign: -1, scaleFactor: 2, value: dVal, paramFrom: dParam.from, paramTo: dParam.to, dimKey: 'diameter', isCenteredParam: true }] });
        }
        break;
      }
    }

    // Generic fallback: auto-map numeric parameters to bbox dimensions for any module.
    // Uses parameter name hints and value matching to create draggable handles.
    if (lines.length === 0 && size.x > 0.001 && size.y > 0.001 && size.z > 0.001) {
      // Find the innermost non-transform module in the chain
      const TRANSFORM_NAMES = new Set(['translate', 'rotate', 'scale', 'color', 'attach', 'align', 'position', 'frame', 'mirror']);
      const genericModule = chain.find(info => !TRANSFORM_NAMES.has(info.moduleName));

      // Collect editable numeric params
      const numParams = genericModule?.params.filter(p => p.type === 'number') ?? [];

      // Map of param name hints to Three.js axes.
      // SCAD X → Three.js X, SCAD Y → Three.js Z, SCAD Z → Three.js Y
      type AxisInfo = { comp: 'x' | 'y' | 'z'; dimKey: string; centered?: boolean };
      const NAME_HINTS: Record<string, AxisInfo> = {
        'width': { comp: 'x', dimKey: 'width' },
        'w': { comp: 'x', dimKey: 'width' },
        'x': { comp: 'x', dimKey: 'width' },
        'sx': { comp: 'x', dimKey: 'width' },
        'height': { comp: 'y', dimKey: 'height' },
        'h': { comp: 'y', dimKey: 'height' },
        'z': { comp: 'y', dimKey: 'height' },
        'sz': { comp: 'y', dimKey: 'height' },
        'depth': { comp: 'z', dimKey: 'depth' },
        'dy': { comp: 'z', dimKey: 'depth' },
        'y': { comp: 'z', dimKey: 'depth' },
        'sy': { comp: 'z', dimKey: 'depth' },
        'length': { comp: 'z', dimKey: 'depth' },
        'l': { comp: 'z', dimKey: 'depth' },
        'r': { comp: 'x', dimKey: 'radius', centered: true },
        'radius': { comp: 'x', dimKey: 'radius', centered: true },
        'd': { comp: 'x', dimKey: 'diameter', centered: true },
        'diameter': { comp: 'x', dimKey: 'diameter', centered: true },
        'size': { comp: 'x', dimKey: 'width' },
      };

      // Axes info for dimension generation
      type DimAxis = { comp: 'x' | 'y' | 'z'; dimKey: string; bboxSize: number };
      const dimAxes: DimAxis[] = [
        { comp: 'x', dimKey: 'width', bboxSize: size.x },
        { comp: 'y', dimKey: 'height', bboxSize: size.y },
        { comp: 'z', dimKey: 'depth', bboxSize: size.z },
      ];
      const assignedAxes = new Set<string>();
      const assignedParams = new Set<string>(); // track by param name

      // Helper: create a dimension line + handle for a param on an axis
      const addGenericDim = (param: ParamField, ax: DimAxis, centered: boolean) => {
        const val = EditorApp.parseParamValue(param);
        if (val === null) return;
        assignedAxes.add(ax.comp);
        assignedParams.add(param.name);

        const axisVec = new THREE.Vector3(
          ax.comp === 'x' ? 1 : 0,
          ax.comp === 'y' ? 1 : 0,
          ax.comp === 'z' ? 1 : 0,
        );

        if (centered) {
          // Radius/diameter: line from center to edge, handles on both sides
          const halfSize = ax.bboxSize / 2;
          const isRadius = ax.dimKey === 'radius' || param.name === 'r' || param.name === 'radius';
          const lineEnd = new THREE.Vector3(center.x, center.y, center.z);
          lineEnd[ax.comp] = isRadius ? max[ax.comp] : max[ax.comp];
          const lineStart = new THREE.Vector3(center.x, center.y, center.z);
          if (!isRadius) lineStart[ax.comp] = min[ax.comp]; // diameter: edge to edge

          const perpDir = new THREE.Vector3(
            ax.comp === 'x' ? 0 : -1,
            ax.comp === 'y' ? 0 : (ax.comp === 'x' ? -1 : 0),
            ax.comp === 'z' ? 0 : 0,
          );
          // Use a reasonable perpendicular
          if (perpDir.lengthSq() < 0.01) perpDir.set(0, -1, 0);

          lines.push({
            start: lineStart, end: lineEnd,
            offsetDir: perpDir, offsetDist: offsetDist + ax.bboxSize * 0.15,
            value: val, dimKey: ax.dimKey, paramFrom: param.from, paramTo: param.to,
          });

          // Radial handles at cardinal points on the bottom face
          const hv = min.y;
          const posMax = new THREE.Vector3(center.x, hv, center.z);
          posMax[ax.comp] = max[ax.comp];
          const posMin = new THREE.Vector3(center.x, hv, center.z);
          posMin[ax.comp] = min[ax.comp];
          handles.push({ position: posMax, axes: [{ axis: axisVec, sign: +1, scaleFactor: 2, value: val, paramFrom: param.from, paramTo: param.to, dimKey: ax.dimKey, isCenteredParam: true }] });
          handles.push({ position: posMin, axes: [{ axis: axisVec, sign: -1, scaleFactor: 2, value: val, paramFrom: param.from, paramTo: param.to, dimKey: ax.dimKey, isCenteredParam: true }] });

          // Also add handles on the perpendicular radius axis if shape is roughly cylindrical
          const perpAx = dimAxes.find(a => a.comp !== ax.comp && a.comp !== 'y' && !assignedAxes.has(a.comp));
          if (perpAx && Math.abs(ax.bboxSize - perpAx.bboxSize) < ax.bboxSize * 0.1) {
            const perpVec = new THREE.Vector3(
              perpAx.comp === 'x' ? 1 : 0,
              perpAx.comp === 'y' ? 1 : 0,
              perpAx.comp === 'z' ? 1 : 0,
            );
            const pMax = new THREE.Vector3(center.x, hv, center.z);
            pMax[perpAx.comp] = max[perpAx.comp];
            const pMin = new THREE.Vector3(center.x, hv, center.z);
            pMin[perpAx.comp] = min[perpAx.comp];
            handles.push({ position: pMax, axes: [{ axis: perpVec, sign: +1, scaleFactor: 2, value: val, paramFrom: param.from, paramTo: param.to, dimKey: ax.dimKey, isCenteredParam: true }] });
            handles.push({ position: pMin, axes: [{ axis: perpVec, sign: -1, scaleFactor: 2, value: val, paramFrom: param.from, paramTo: param.to, dimKey: ax.dimKey, isCenteredParam: true }] });
          }
        } else {
          // Linear dimension: line along axis at bbox edge, handle at max end
          const lineStart = new THREE.Vector3(min.x, min.y, max.z);
          const lineEnd = new THREE.Vector3(min.x, min.y, max.z);
          lineStart[ax.comp] = min[ax.comp];
          lineEnd[ax.comp] = max[ax.comp];

          const perpDir = ax.comp === 'y'
            ? new THREE.Vector3(-1, 0, 0)
            : ax.comp === 'x'
              ? new THREE.Vector3(0, 0, 1)
              : new THREE.Vector3(1, 0, 0);

          lines.push({
            start: lineStart, end: lineEnd,
            offsetDir: perpDir, offsetDist,
            value: val, dimKey: ax.dimKey, paramFrom: param.from, paramTo: param.to,
          });

          // Handle at the "max" end of this axis
          const handlePos = new THREE.Vector3(center.x, max.y, center.z);
          if (ax.comp !== 'y') handlePos.y = min.y;
          handlePos[ax.comp] = max[ax.comp];
          handles.push({
            position: handlePos,
            axes: [{ axis: axisVec, sign: +1, scaleFactor: 1, value: val, paramFrom: param.from, paramTo: param.to, dimKey: ax.dimKey, isCenteredParam: false }],
          });
        }
      };

      // Pass 1: Name-based matching
      for (const param of numParams) {
        const hint = NAME_HINTS[param.name.toLowerCase()];
        if (!hint || assignedAxes.has(hint.comp)) continue;
        const ax = dimAxes.find(a => a.comp === hint.comp);
        if (ax) addGenericDim(param, ax, hint.centered ?? false);
      }

      // Pass 2: Value-based matching for remaining params
      for (const param of numParams) {
        if (assignedParams.has(param.name)) continue;
        const val = EditorApp.parseParamValue(param);
        if (val === null || val <= 0) continue;

        // Try to match param value to an unassigned bbox dimension
        for (const ax of dimAxes) {
          if (assignedAxes.has(ax.comp)) continue;
          const tolerance = Math.max(ax.bboxSize * 0.02, 0.05);
          if (Math.abs(val - ax.bboxSize) < tolerance) {
            // Direct match: param value ≈ bbox dimension
            addGenericDim(param, ax, false);
            break;
          }
          if (Math.abs(val * 2 - ax.bboxSize) < tolerance) {
            // Half match: param is radius/half-size of bbox dimension
            addGenericDim(param, ax, true);
            break;
          }
        }
      }

      // Pass 3: Show read-only dimension lines for any unassigned bbox axes
      for (const ax of dimAxes) {
        if (assignedAxes.has(ax.comp)) continue;
        const lineStart = new THREE.Vector3(min.x, min.y, max.z);
        const lineEnd = new THREE.Vector3(min.x, min.y, max.z);
        lineStart[ax.comp] = min[ax.comp];
        lineEnd[ax.comp] = max[ax.comp];

        const perpDir = ax.comp === 'y'
          ? new THREE.Vector3(-1, 0, 0)
          : ax.comp === 'x'
            ? new THREE.Vector3(0, 0, 1)
            : new THREE.Vector3(1, 0, 0);

        lines.push({
          start: lineStart, end: lineEnd,
          offsetDir: perpDir, offsetDist,
          value: parseFloat(ax.bboxSize.toFixed(2)),
          dimKey: ax.dimKey,
          paramFrom: 0, paramTo: 0, // read-only
        });
      }
    }

    return { handles, lines, workPlaneY: min.y, bbox: bbox.clone() };
  }

  onParamChange(callback: (infos: ParamInfo[] | null) => void): void {
    this.paramCallback = callback;
  }

  onTransformUpdate(callback: (info: TransformInfo | null) => void): void {
    this.transformCallback = callback;
  }

  /** Fires whenever the viewport selection changes. from/to are null on clear. */
  /** Subscribe to selection changes (multiple listeners allowed). */
  onSelectionChange(callback: (from: SourcePosition | null, to: SourcePosition | null) => void): void {
    this.selectionCallbacks.push(callback);
  }

  /** Recompute and fire transform info from current bbox (used during gizmo drag). */
  private fireTransformInfoFromBBox(): void {
    if (!this.transformCallback) return;
    const geoInfo = this.viewport.getSelectedBBox();
    if (!geoInfo) return;
    const center = new THREE.Vector3();
    geoInfo.bbox.getCenter(center);
    const o = this.viewport.getUserOrigin();
    const relativeToOrigin = o.lengthSq() > 1e-6;
    const rel = relativeToOrigin
      ? new THREE.Vector3().subVectors(center, o)
      : center;
    const position: [number, number, number] = [rel.x, -rel.z, rel.y];

    // Combine SCAD source rotation with live gizmo rotation delta
    let rotation: [number, number, number] | null = this.lastRotation;
    const meshRot = this.viewport.getSelectedMeshRotation();
    if (meshRot && (Math.abs(meshRot.x) > 0.001 || Math.abs(meshRot.y) > 0.001 || Math.abs(meshRot.z) > 0.001)) {
      const toDeg = (rad: number) => rad * 180 / Math.PI;
      // Three.js Y-up → SCAD Z-up: same conversion as handleTransformEnd
      const deltaRx = toDeg(meshRot.x);
      const deltaRy = toDeg(-meshRot.z);
      const deltaRz = toDeg(meshRot.y);
      const base = this.lastRotation ?? [0, 0, 0];
      rotation = [base[0] + deltaRx, base[1] + deltaRy, base[2] + deltaRz];
    }

    this.transformCallback({ position, rotation, relativeToOrigin, size: scadSize(geoInfo.bbox) });
  }

  /** Rebuild & push dimensions for the current selection (respects origin + toggle). */
  private redrawDimensions(): void {
    const chain = this.lastDimChain;
    if (!chain) { this.viewport.clearDimensions(); return; }
    const geoInfo = this.viewport.getSelectedBBox();
    if (!geoInfo) { this.viewport.clearDimensions(); return; }
    const setup = this.computeDimensions(chain, geoInfo.bbox, geoInfo.center);
    this.appendOriginDimensions(setup, geoInfo.bbox);
    if (setup.lines.length > 0 || setup.handles.length > 0) {
      this.viewport.showDimensions(setup);
    } else {
      this.viewport.clearDimensions();
    }
  }

  /**
   * Append three origin→bbox-center axis-aligned dimension lines (one per SCAD axis).
   * Only adds lines for axes where |delta| exceeds a small threshold and
   * only when the user origin is non-zero and the toggle is on.
   */
  private appendOriginDimensions(setup: DimensionSetup, bbox: THREE.Box3): void {
    if (!this.showOriginDims) return;
    const origin = this.viewport.getUserOrigin();
    if (origin.lengthSq() < 1e-6) return;

    const center = new THREE.Vector3();
    bbox.getCenter(center);
    const delta = new THREE.Vector3().subVectors(center, origin);

    const size = new THREE.Vector3();
    bbox.getSize(size);
    const diag = size.length();
    const offsetDist = Math.max(0.3, diag * 0.06);
    const MIN_DELTA = 0.01;

    // Three.js axes. SCAD label mapping: X→ΔX, Y→ΔZ (y-up = SCAD Z), Z→ΔY negated.
    const axes: Array<{
      dir: THREE.Vector3;
      offDir: THREE.Vector3;
      scadValue: number;
      label: string;
      key: string;
    }> = [
      { dir: new THREE.Vector3(1, 0, 0), offDir: new THREE.Vector3(0, 0, 1), scadValue: delta.x, label: 'ΔX', key: 'origin-dx' },
      { dir: new THREE.Vector3(0, 0, 1), offDir: new THREE.Vector3(1, 0, 0), scadValue: -delta.z, label: 'ΔY', key: 'origin-dy' },
      { dir: new THREE.Vector3(0, 1, 0), offDir: new THREE.Vector3(1, 0, 0), scadValue: delta.y, label: 'ΔZ', key: 'origin-dz' },
    ];

    // Count the axes with a non-negligible delta. If the offset is purely
    // along one axis, the Euclidean "D" line would duplicate that ΔN, so we
    // skip it. Only show the diagonal when the origin→object vector is
    // genuinely non-axial (2+ axes contribute).
    const significantAxes = axes.filter(a => Math.abs(a.scadValue) >= MIN_DELTA).length;

    // Walk each SCAD axis: start at origin, step along this axis by its delta.
    // Subsequent axes start from the tip of the previous step, so the three
    // segments form an L-shaped polyline from origin to the object's center.
    const cursor = origin.clone();
    for (const a of axes) {
      if (Math.abs(a.scadValue) < MIN_DELTA) continue;
      const componentMag = a.dir.x !== 0 ? delta.x : a.dir.y !== 0 ? delta.y : delta.z;
      const start = cursor.clone();
      const end = cursor.clone().addScaledVector(a.dir, componentMag);
      setup.lines.push({
        start,
        end,
        offsetDir: a.offDir,
        offsetDist,
        value: a.scadValue,
        dimKey: a.key,
        paramFrom: -1,
        paramTo: -1,
        readonly: true,
        labelPrefix: a.label,
      });
      cursor.copy(end);
    }

    // Direct (Euclidean) distance line from origin → bbox center.
    // Only when the vector spans 2+ axes (otherwise D duplicates a ΔN).
    const distance = delta.length();
    if (distance >= MIN_DELTA && significantAxes >= 2) {
      const dir = delta.clone().normalize();
      // Offset direction: perpendicular to the line, preferring the horizontal
      // plane so the label floats above/beside the line rather than clipping it.
      const worldUp = new THREE.Vector3(0, 1, 0);
      let offDir = new THREE.Vector3().crossVectors(dir, worldUp);
      if (offDir.lengthSq() < 1e-4) {
        // Line is (near-)vertical → use world X as the offset basis
        offDir = new THREE.Vector3().crossVectors(dir, new THREE.Vector3(1, 0, 0));
      }
      offDir.normalize();
      setup.lines.push({
        start: origin.clone(),
        end: center.clone(),
        offsetDir: offDir,
        offsetDist: offsetDist * 1.5,
        value: distance,
        dimKey: 'origin-dist',
        paramFrom: -1,
        paramTo: -1,
        readonly: true,
        labelPrefix: 'D',
      });
    }
  }

  /** Toggle origin→object dimension lines. Persists + refreshes. */
  setShowOriginDims(v: boolean): void {
    this.showOriginDims = v;
    try { localStorage.setItem('scadplus-origin-dims', v ? '1' : '0'); } catch { /* ignore */ }
    this.redrawDimensions();
  }

  getShowOriginDims(): boolean {
    return this.showOriginDims;
  }

  updateParam(from: number, to: number, newValue: string): void {
    // Remember current selection so we can restore it after pipeline rebuild
    const key = this.viewport.getSelectedKey();

    const code = this.editor.getCode();
    this.applyCodeChange(code.substring(0, from) + newValue + code.substring(to));

    // Re-select the same part/child (same module instance) once the new meshes
    // exist (fires childSelectedCallback → refreshes panel). Edits before the
    // selection shift its offset.
    if (key) this.reselectAfterRender(key.offset + shiftFor(key.offset, from, to, newValue.length), key.instance);
  }

  private reselectAfterRender(offset: number, instance?: string): void {
    this.afterNextRender(() => {
      if (!this.viewport.selectBySourceOffset(offset, instance)) {
        // Re-selection failed — clear panel to prevent stale from/to closures
        if (this.paramCallback) this.paramCallback(null);
      }
    });
  }

  /** Apply a programmatic rewrite of the whole document as ONE minimal,
   *  undoable edit (only the changed span is replaced, so Ctrl+Z restores
   *  the previous code and the cursor/scroll stay put), then re-render. */
  applyCodeChange(newCode: string): void {
    const oldCode = this.editor.getCode();
    if (oldCode !== newCode) {
      const { from, to, text } = minimalEdit(oldCode, newCode);
      // replaceRange → updateListener → onChange fires synchronously; skip
      // that debounced run, we render immediately below.
      this.suppressNextCodeChange = true;
      this.editor.replaceRange(from, to, text);
    }
    this.runPipeline(newCode);
  }

  // Incremental source rewrite that preserves undo history (single
  // dispatch() into the existing EditorState instead of setCode's wholesale
  // state replacement). Use for sketch-editor entity edits, AI snippet
  // applies, dim-overlay commits — anything the user should be able to
  // Ctrl+Z. The pipeline re-runs from the onChange listener.
  replaceRange(from: number, to: number, text: string): void {
    this.editor.replaceRange(from, to, text);
  }

  // --- Manifold Memory Management ---

  private freeManifolds(): void {
    for (const p of this.lastParts) {
      // Free child manifolds first
      if (p.children) {
        for (const child of p.children) {
          try { child.manifold.delete(); } catch { /* ok */ }
          if (child.fullManifold) {
            try { (child.fullManifold as InstanceType<ManifoldToplevel['Manifold']>).delete(); } catch { /* ok */ }
          }
        }
      }
      for (const d of p.display ?? []) {
        try { (d.manifold as InstanceType<ManifoldToplevel['Manifold']>).delete(); } catch { /* shared with a child / already freed */ }
      }
      try { p.manifold.delete(); } catch { /* ok */ }
    }
    this.lastParts = [];

    if (this.lastManifold) {
      try { this.lastManifold.delete(); } catch { /* ok */ }
      this.lastManifold = null;
    }
  }

  // --- Public API ---

  loadExample(index: number): void {
    if (index < 0 || index >= SCAD_EXAMPLES.length) return;
    this.viewport.refitNextGeometry();
    this.editor.setCode(SCAD_EXAMPLES[index].code);
    this.runPipeline(SCAD_EXAMPLES[index].code);
  }

  setFn(fn: number): void {
    this.fn = fn;
    this.runPipeline(this.editor.getCode());
  }

  goToLine(line: number, col: number): void {
    this.editor.goToLine(line, col);
  }

  resetCamera(): void {
    this.viewport.resetCamera();
  }

  setTheme(mode: ThemeMode): void {
    this.editor.setTheme(mode);
    this.viewport.applyTheme(getViewportTheme(mode));
  }

  getViewport(): ViewportManager {
    return this.viewport;
  }

  toggleWireframe(): boolean {
    return this.viewport.toggleWireframe();
  }

  getAST(): Program | null {
    return this.lastProgram;
  }

  getCode(): string {
    return this.editor.getCode();
  }

  setCode(code: string): void {
    this.editor.setCode(code);
  }

  onCursorChange(cb: (offset: number) => void): void {
    this.editor.onCursorChange(cb);
  }

  undo(): boolean { return this.editor.undo(); }
  redo(): boolean { return this.editor.redo(); }

  /** Deletes the currently selected shape's code from the editor. Returns true if something was deleted. */
  deleteSelection(): boolean {
    const range = this.viewport.getSelectedRange();
    if (!range) return false;

    const code = this.editor.getCode();
    let { from, to } = range;

    // Expand selection to include trailing whitespace/newline and semicolon
    while (to < code.length && (code[to] === ' ' || code[to] === '\t')) to++;
    if (to < code.length && code[to] === ';') to++;
    while (to < code.length && (code[to] === ' ' || code[to] === '\t')) to++;
    if (to < code.length && code[to] === '\n') to++;

    // Also try to consume leading whitespace on the line
    while (from > 0 && (code[from - 1] === ' ' || code[from - 1] === '\t')) from--;

    this.viewport.deselectAll();
    this.applyCodeChange(code.substring(0, from) + code.substring(to));
    return true;
  }

  loadCode(code: string): void {
    this.viewport.refitNextGeometry();
    this.editor.setCode(code);
    this.runPipeline(code);
  }

  getExamples(): typeof SCAD_EXAMPLES {
    return SCAD_EXAMPLES;
  }

  // --- STEP Import ---

  async importSTEP(filename: string, data: Uint8Array): Promise<void> {
    if (!this.wasm) throw new Error('WASM not initialized');

    const { Manifold, Mesh } = this.wasm;

    // Lazy-load occt-import-js via script tag (CJS/UMD module doesn't bundle well with Vite)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis as any;
    if (!g.occtimportjs) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/node_modules/occt-import-js/dist/occt-import-js.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load occt-import-js script'));
        document.head.appendChild(script);
      });
    }
    if (typeof g.occtimportjs !== 'function') {
      throw new Error('occt-import-js loaded but factory function not found');
    }
    const occt = await g.occtimportjs();

    // Parse the STEP file
    const result = occt.ReadStepFile(data, null);
    if (!result.success || !result.meshes || result.meshes.length === 0) {
      throw new Error('Failed to parse STEP file or file contains no geometry');
    }

    // Collect all vertex/index data across meshes and merge into one Manifold
    const manifolds: InstanceType<ManifoldToplevel['Manifold']>[] = [];

    for (const mesh of result.meshes) {
      const positions = mesh.attributes.position.array;
      const indices = mesh.index.array;

      if (positions.length === 0 || indices.length === 0) continue;

      const vertProperties = new Float32Array(positions);
      const triVerts = new Uint32Array(indices);

      try {
        const manifoldMesh = new Mesh({
          numProp: 3,
          vertProperties,
          triVerts,
          mergeFromVert: undefined,
          mergeToVert: undefined,
        });
        const m = new Manifold(manifoldMesh);
        manifolds.push(m);
      } catch {
        // Non-manifold mesh — try with merge
        try {
          const manifoldMesh = new Mesh({
            numProp: 3,
            vertProperties,
            triVerts,
          });
          // Attempt merge to fix non-manifold issues
          const merged = manifoldMesh.merge();
          void merged; // merge mutates in-place, returns void
          const m = new Manifold(manifoldMesh);
          manifolds.push(m);
        } catch {
          // Skip this mesh
          console.warn(`Skipping non-manifold mesh in ${filename}`);
        }
      }
    }

    if (manifolds.length === 0) {
      throw new Error('No valid geometry could be created from STEP file. The mesh may be non-manifold.');
    }

    // Union all parts into a single Manifold
    let combined = manifolds[0];
    for (let i = 1; i < manifolds.length; i++) {
      const next = combined.add(manifolds[i]);
      combined.delete();
      manifolds[i].delete();
      combined = next;
    }

    // Free any previous import with the same name
    const prev = this.importedManifolds.get(filename);
    if (prev) {
      try { prev.delete(); } catch { /* ok */ }
    }

    this.importedManifolds.set(filename, combined);

    // Persist to IndexedDB for reload survival
    const meshData = combined.getMesh();
    saveMesh({
      filename,
      numProp: meshData.numProp,
      vertProperties: new Float32Array(meshData.vertProperties),
      triVerts: new Uint32Array(meshData.triVerts),
    }).catch(e => console.warn('Failed to persist imported mesh:', e));

    // Re-run pipeline so import() picks up the new manifold
    this.viewport.refitNextGeometry();
    this.runPipeline(this.editor.getCode());
    window.umami?.track('import_mesh', { filename });
  }

  // --- STL Import ---

  async importSTL(filename: string, data: ArrayBuffer): Promise<void> {
    if (!this.wasm) throw new Error('WASM not initialized');

    const { Manifold, Mesh } = this.wasm;

    // Lazy-load STLLoader from three.js examples
    const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
    const loader = new STLLoader();
    const geometry = loader.parse(data);

    // STLLoader produces a non-indexed BufferGeometry with position attribute
    const posAttr = geometry.getAttribute('position');
    if (!posAttr || posAttr.count === 0) {
      throw new Error('STL file contains no geometry');
    }

    const vertCount = posAttr.count;
    const vertProperties = new Float32Array(vertCount * 3);
    for (let i = 0; i < vertCount; i++) {
      vertProperties[i * 3] = posAttr.getX(i);
      vertProperties[i * 3 + 1] = posAttr.getY(i);
      vertProperties[i * 3 + 2] = posAttr.getZ(i);
    }

    // STL is non-indexed: sequential triangle indices
    const triVerts = new Uint32Array(vertCount);
    for (let i = 0; i < vertCount; i++) triVerts[i] = i;

    let manifold: InstanceType<ManifoldToplevel['Manifold']>;
    try {
      const mesh = new Mesh({ numProp: 3, vertProperties, triVerts });
      manifold = new Manifold(mesh);
    } catch {
      // Non-manifold — try with merge
      try {
        const mesh = new Mesh({ numProp: 3, vertProperties, triVerts });
        mesh.merge();
        manifold = new Manifold(mesh);
      } catch {
        throw new Error('Failed to create manifold from STL. The mesh may be non-manifold.');
      }
    }

    // Center the manifold at the origin so the imported object isn't offset
    const bbox = manifold.boundingBox();
    const cx = (bbox.min[0] + bbox.max[0]) / 2;
    const cy = (bbox.min[1] + bbox.max[1]) / 2;
    const cz = (bbox.min[2] + bbox.max[2]) / 2;
    if (cx !== 0 || cy !== 0 || cz !== 0) {
      const centered = manifold.translate([-cx, -cy, -cz]);
      manifold.delete();
      manifold = centered;
    }

    // Free any previous import with the same name
    const prev = this.importedManifolds.get(filename);
    if (prev) {
      try { prev.delete(); } catch { /* ok */ }
    }

    this.importedManifolds.set(filename, manifold);

    // Persist to IndexedDB for reload survival
    const meshData = manifold.getMesh();
    saveMesh({
      filename,
      numProp: meshData.numProp,
      vertProperties: new Float32Array(meshData.vertProperties),
      triVerts: new Uint32Array(meshData.triVerts),
    }).catch(e => console.warn('Failed to persist imported mesh:', e));

    // Re-run pipeline so import() picks up the new manifold
    this.viewport.refitNextGeometry();
    this.runPipeline(this.editor.getCode());
    window.umami?.track('import_mesh', { filename });
  }

  // --- STEP → SCAD Conversion ---

  async importSTEPtoScad(data: Uint8Array): Promise<string> {
    // Lazy-load occt-import-js via script tag (same pattern as importSTEP)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const g = globalThis as any;
    if (!g.occtimportjs) {
      await new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = '/node_modules/occt-import-js/dist/occt-import-js.js';
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load occt-import-js script'));
        document.head.appendChild(script);
      });
    }
    if (typeof g.occtimportjs !== 'function') {
      throw new Error('occt-import-js loaded but factory function not found');
    }
    const occt = await g.occtimportjs();

    const result = occt.ReadStepFile(data, null);
    if (!result.success || !result.meshes || result.meshes.length === 0) {
      throw new Error('Failed to parse STEP file or file contains no geometry');
    }

    const { scad, recognized, total } = stepMeshesToScad(result.meshes);
    console.log(`STEP→SCAD: recognized ${recognized}/${total} B-rep faces`);
    return scad;
  }

  /** Returns list of currently loaded import filenames */
  getImportedFiles(): string[] {
    return [...this.importedManifolds.keys()];
  }

  private freeImportedManifolds(): void {
    for (const m of this.importedManifolds.values()) {
      try { m.delete(); } catch { /* ok */ }
    }
    this.importedManifolds.clear();
  }

  dispose(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.freeManifolds();
    this.freeImportedManifolds();
    this.editor.destroy();
    this.viewport.dispose();
  }
}

type ManifoldBox = { min: ArrayLike<number>; max: ArrayLike<number> };

function toScadBounds(b: ManifoldBox): ScadBounds {
  return { min: [b.min[0], b.min[1], b.min[2]], max: [b.max[0], b.max[1], b.max[2]] };
}

function unionBounds(boxes: ManifoldBox[]): ScadBounds | null {
  if (boxes.length === 0) return null;
  const r = toScadBounds(boxes[0]);
  for (const b of boxes.slice(1)) {
    for (let i = 0; i < 3; i++) {
      r.min[i] = Math.min(r.min[i], b.min[i]);
      r.max[i] = Math.max(r.max[i], b.max[i]);
    }
  }
  return r;
}

function metricsOf(m: { boundingBox(): ManifoldBox; volume(): number; surfaceArea(): number }): SolidMetrics {
  return { bounds: toScadBounds(m.boundingBox()), volume: m.volume(), area: m.surfaceArea() };
}

/** Size of a Three.js (Y-up) box in SCAD axes (X, Y, Z). */
function scadSize(box: THREE.Box3): [number, number, number] {
  const s = new THREE.Vector3();
  box.getSize(s);
  return [s.x, s.z, s.y];
}

/** Viewport geometry of a selection ghost: its manifold with the ghost matrix applied. */
function ghostGeometry(part: GeometryPart): THREE.BufferGeometry {
  const geometry = manifoldToGeometry(part.manifold, false);
  if (!part.matrix) return geometry;
  const m = new THREE.Matrix4().fromArray(part.matrix);
  geometry.applyMatrix4(m);
  // A mirroring matrix turns the triangles inside out — restore the winding
  if (m.determinant() < 0) {
    for (const name of ['position', 'normal'] as const) {
      const attr = geometry.getAttribute(name) as THREE.BufferAttribute | undefined;
      if (!attr) continue;
      const a = attr.array as Float32Array;
      for (let t = 0; t + 9 <= a.length; t += 9) {
        for (let k = 0; k < 3; k++) { const tmp = a[t + 3 + k]; a[t + 3 + k] = a[t + 6 + k]; a[t + 6 + k] = tmp; }
      }
      attr.needsUpdate = true;
    }
  }
  return geometry;
}

/** Metrics of a selection ghost in world space (ghost matrix applied). */
function ghostMetrics(part: GeometryPart): SolidMetrics {
  const { vertProperties: v, triVerts: t, numProp: np } = partWorldMesh(part);
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < v.length; i += np) for (let k = 0; k < 3; k++) {
    min[k] = Math.min(min[k], v[i + k]);
    max[k] = Math.max(max[k], v[i + k]);
  }
  let area = 0;
  for (let i = 0; i < t.length; i += 3) {
    const a = t[i] * np, b = t[i + 1] * np, c = t[i + 2] * np;
    const ux = v[b] - v[a], uy = v[b + 1] - v[a + 1], uz = v[b + 2] - v[a + 2];
    const wx = v[c] - v[a], wy = v[c + 1] - v[a + 1], wz = v[c + 2] - v[a + 2];
    area += 0.5 * Math.hypot(uy * wz - uz * wy, uz * wx - ux * wz, ux * wy - uy * wx);
  }
  const det = Math.abs(new THREE.Matrix4().fromArray(part.matrix!).determinant());
  return { bounds: { min, max }, volume: part.manifold.volume() * det, area };
}

/** How far an edit replacing [from, to) with `len` chars moves `offset`. */
function shiftFor(offset: number, from: number, to: number, len: number): number {
  return offset >= to ? len - (to - from) : 0;
}
